import React, { useState, useEffect, useMemo } from 'react';
import { ServiceSession, UserRole, Client, Provider, UserSession, ClinicalDocument } from '../types';
import { ArcSearchInput } from './ArcSearchInput';
import { GUARDIAN_CLOCK_MISSING_MESSAGE, GUARDIAN_OVERDUE_PAY_MESSAGE, assessGuardianPayWindow } from '../utils/guardianEncounterDeadline';
import { readDisplaySync } from '../savedDisplaySync';
import {
  Users,
  UserCheck,
  UserPlus,
  CheckCircle,
  Plus,
  Clock,
  Search,
  Filter,
  Calendar,
  AlertTriangle,
  FileText,
  Edit2,
  X,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Shield,
  Trash2,
  Stethoscope,
  Check,
  ExternalLink
} from 'lucide-react';
import { AddTherapistModal } from './AddTherapistModal';
import { canEditManualSoapApproval, ManualSoapApprovalModal } from './ManualSoapApprovalModal';
import {
  AllegiantPayPeriod,
  getCurrentPayPeriod,
  getAdjacentPayPeriod,
  getRecentPayPeriods,
  isDateInPayPeriod,
  formatDisplayDate
} from '../utils/payPeriod';

interface AssignmentsServicesViewProps {
  user?: UserSession;
  userRole: UserRole;
  onNavigateTab: (tab: string) => void;
  onSelectClient: (clientId: string) => void;
  onRefreshStats?: () => void;
}

interface ActiveSessionPresenceItem {
  providerId: string;
  providerName: string;
  clientId: string;
  clientName: string;
  startTimestamp: number;
  serviceCode: string;
  isActive: boolean;
  lastHeartbeat: number;
  authException?: string | null;
}

// Frequency helpers
function parseWeeklyFrequency(freqStr?: string): { count: number; isWeekly: boolean; label: string } {
  if (!freqStr) return { count: 0, isWeekly: false, label: 'Unset' };
  const clean = freqStr.trim().toLowerCase();
  if (clean.includes('per auth') || clean.includes('per authorization') || clean.includes('total')) {
    return { count: 0, isWeekly: false, label: freqStr };
  }
  const matchX = clean.match(/(\d+)\s*(?:x|\/|\s*sessions?|\s*visits?)\s*(?:per|\/)?\s*(?:wk|week|weekly)?/);
  if (matchX && matchX[1]) {
    const num = parseInt(matchX[1], 10);
    if (!isNaN(num) && num > 0 && num <= 7) {
      return { count: num, isWeekly: true, label: `${num}x/wk` };
    }
  }
  return { count: 0, isWeekly: false, label: freqStr };
}

function getWeekRangeForDate(dateStr: string): { start: string; end: string } {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    start: monday.toISOString().slice(0, 10),
    end: sunday.toISOString().slice(0, 10)
  };
}

function getWeeklySessionStats(clientId: string, targetDos: string, servicesList: any[], assignmentsList: any[]) {
  const weekRange = getWeekRangeForDate(targetDos);
  const clientServicesInWeek = servicesList.filter(s =>
    s.client_id === clientId &&
    s.dos >= weekRange.start &&
    s.dos <= weekRange.end &&
    (s.status === 'SCHEDULED' || s.status === 'COMPLETED')
  );

  const plannedCount = clientServicesInWeek.filter(s => s.status === 'SCHEDULED').length;
  const completedCount = clientServicesInWeek.filter(s => s.status === 'COMPLETED').length;
  const totalCount = plannedCount;

  const asn = assignmentsList.find(a => a.client_id === clientId);
  const rawFreq = asn?.expected_frequency || asn?.auth_frequency || '';
  const freqInfo = parseWeeklyFrequency(rawFreq);

  return {
    weekRange,
    plannedCount,
    completedCount,
    totalCount,
    freqInfo,
    rawFreq,
    exceeds: freqInfo.isWeekly && totalCount > freqInfo.count
  };
}

function formatPlannerVisitDate(dosStr?: string): string {
  if (!dosStr) return '—';
  try {
    const parts = dosStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const d = new Date(year, month, day);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    const d = new Date(dosStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dosStr;
  }
}

function formatPlannerVisitTime(timeStr?: string): string {
  if (!timeStr) return '';
  try {
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1].padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${ampm}`;
    }
    return timeStr;
  } catch {
    return timeStr;
  }
}

export const AssignmentsServicesView: React.FC<AssignmentsServicesViewProps> = ({
  user,
  userRole,
  onNavigateTab,
  onSelectClient,
  onRefreshStats
}) => {
  const isSupervisor = userRole === 'SUPERVISOR';
  const isAssistant = userRole === 'ASSISTANT';
  const isClinicianOrAssistant = isAssistant;
  const isOperationalRole = userRole === 'OWNER' || userRole === 'ADMIN' || userRole === 'REFERRAL_COORDINATOR' || userRole === 'OFFICE';
  const canManualServiceEntry = isOperationalRole;

  const [activeTab, setActiveTab] = useState<'RELATIONSHIPS' | 'COMPLETED_SERVICES' | 'CALENDAR'>(
    isAssistant ? 'CALENDAR' : 'RELATIONSHIPS'
  );
  const [relationshipSubTab, setRelationshipSubTab] = useState<'CLIENT_ASSIGNMENTS' | 'SUPERVISOR_OVERSIGHT' | 'BY_PROVIDER'>('CLIENT_ASSIGNMENTS');
  
  const [assignments, setAssignments] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedProviderFilter, setSelectedProviderFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedAssistants, setCollapsedAssistants] = useState<Record<string, boolean>>({});

  // Canonical Visit Log State: Provider + Pay Period + Search
  const [selectedPayPeriod, setSelectedPayPeriod] = useState<AllegiantPayPeriod | 'ALL'>(() => getCurrentPayPeriod());
  const [selectedVisitLogProviderId, setSelectedVisitLogProviderId] = useState<string>('ALL');
  const [visitLogSearchQuery, setVisitLogSearchQuery] = useState<string>('');
  const [isProviderDropdownOpen, setIsProviderDropdownOpen] = useState(false);
  const [providerSearchTerm, setProviderSearchTerm] = useState('');
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const [clinicalDocs, setClinicalDocs] = useState<ClinicalDocument[]>([]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#provider-filter-popover') && !target.closest('#provider-filter-button')) {
        setIsProviderDropdownOpen(false);
      }
      if (!target.closest('#period-filter-popover') && !target.closest('#period-filter-button')) {
        setIsPeriodDropdownOpen(false);
      }
    };
    if (isProviderDropdownOpen || isPeriodDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isProviderDropdownOpen, isPeriodDropdownOpen]);

  // Minimal Live Session Presence (Observability only)
  const [liveSessions, setLiveSessions] = useState<ActiveSessionPresenceItem[]>([]);
  const [, setPresenceTick] = useState(0);

  useEffect(() => {
    // Only query presence for roles authorized for live session visibility (OWNER, ADMIN, SUPERVISOR)
    const isAuthorizedForPresence = userRole === 'OWNER' || userRole === 'ADMIN' || userRole === 'SUPERVISOR';
    if (!isAuthorizedForPresence) {
      return;
    }

    let isMounted = true;
    const fetchPresence = async () => {
      try {
        const res = await fetch('/api/sessions/presence');
        if (res.ok && isMounted) {
          const data = await res.json();
          setLiveSessions(Array.isArray(data.sessions) ? data.sessions : []);
        }
      } catch {
        // Quiet non-blocking observability fetch
      }
    };

    fetchPresence();
    const interval = setInterval(fetchPresence, 8000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [userRole]);

  // Periodic UI tick to keep elapsed minutes fresh while sessions are active
  useEffect(() => {
    if (liveSessions.length === 0) return;
    const interval = setInterval(() => {
      setPresenceTick(t => t + 1);
    }, 15000);
    return () => clearInterval(interval);
  }, [liveSessions.length]);

  const toggleAssistantCollapse = (assistantId: string) => {
    setCollapsedAssistants(prev => ({
      ...prev,
      [assistantId]: !prev[assistantId]
    }));
  };

  useEffect(() => {
    if (isClinicianOrAssistant && activeTab === 'RELATIONSHIPS') {
      setActiveTab('CALENDAR');
    }
  }, [userRole, isClinicianOrAssistant]);

  // Assign Modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({
    client_id: '',
    provider_id: '',
    supervisor_id: '',
    expected_frequency: '2x/week',
    expected_duration_minutes: 60,
    notes: ''
  });

  // Plan Visit Modal
  const [showPlanVisitModal, setShowPlanVisitModal] = useState(false);
  const [planVisitSavedId, setPlanVisitSavedId] = useState<string | null>(null);
  const [planVisitSyncWarning, setPlanVisitSyncWarning] = useState<string | null>(null);
  const [planVisitForm, setPlanVisitForm] = useState({
    client_id: '',
    provider_id: '',
    dos: new Date().toISOString().slice(0, 10),
    start_time: '10:00',
    duration_minutes: 60,
    service_code: '92507',
    completion_notes: 'Planned therapy visit.'
  });

  // Record Completed Service Modal
  const [showRecordServiceModal, setShowRecordServiceModal] = useState(false);
  const [recordServiceSavedId, setRecordServiceSavedId] = useState<string | null>(null);
  const [recordServiceSyncWarning, setRecordServiceSyncWarning] = useState<string | null>(null);
  const [recordServiceForm, setRecordServiceForm] = useState({
    client_id: '',
    provider_id: '',
    dos: new Date().toISOString().slice(0, 10),
    service_code: '92507',
    planned_duration_minutes: 60,
    actual_duration_minutes: 60,
    completion_notes: 'Speech therapy session completed.'
  });

  // Add Therapist Modal
  const [showAddTherapistModal, setShowAddTherapistModal] = useState(false);
  const [therapistSuccessBanner, setTherapistSuccessBanner] = useState<string | null>(null);

  const [manualSoapAssignment, setManualSoapAssignment] = useState<any>(null);

  const isOfficeOrAdmin = userRole === 'ADMIN' || userRole === 'OFFICE';
  const canEditRemoteSignature = canEditManualSoapApproval(userRole);

  const handleTherapistAdded = async (newProvider: Provider) => {
    // Refresh all assignments, services, clients, and providers
    await fetchData();
    onRefreshStats?.();
    setTherapistSuccessBanner(`Therapist "${newProvider.name}" (${newProvider.role}) added successfully and is ready for client assignments.`);
    setTimeout(() => {
      setTherapistSuccessBanner(null);
    }, 7000);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [asnRes, srvRes, cliRes, provRes, docsRes] = await Promise.all([
        fetch('/api/assignments'),
        fetch('/api/services'),
        fetch('/api/clients'),
        fetch('/api/providers'),
        fetch('/api/clinical/docs')
      ]);
      if (asnRes.ok) setAssignments(await asnRes.json());
      if (srvRes.ok) setServices(await srvRes.json());
      if (cliRes.ok) setClients(await cliRes.json());
      if (provRes.ok) setProviders(await provRes.json());
      if (docsRes && docsRes.ok) setClinicalDocs(await docsRes.json());
    } catch (err) {
      console.error('Error loading assignments data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleProviderSelectInForm = (providerId: string) => {
    const prov = providers.find(p => p.id === providerId);
    setAssignForm(prev => ({
      ...prev,
      provider_id: providerId,
      supervisor_id: (prov as any)?.supervisor_id || ''
    }));
  };

  const handleSaveAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignForm.client_id || !assignForm.provider_id) return;
    try {
      const res = await fetch('/api/assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignForm)
      });
      if (res.ok) {
        setShowAssignModal(false);
        setAssignForm({
          client_id: '',
          provider_id: '',
          supervisor_id: '',
          expected_frequency: '2x/week',
          expected_duration_minutes: 60,
          notes: ''
        });
        await fetchData();
      }
    } catch (err) {
      console.error('Error saving assignment:', err);
    }
  };

  const openPlanVisitModal = (prefillClientId?: string, prefillProviderId?: string) => {
    const activeClients = clients.filter(c => (c.status || 'ACTIVE').toUpperCase() === 'ACTIVE');
    const targetClient = activeClients.find(c => c.id === prefillClientId) || activeClients[0];
    const targetCid = targetClient?.id || '';
    const asn = assignments.find(a => a.client_id === targetCid);
    const provId = prefillProviderId || asn?.provider_id || (isClinicianOrAssistant && myProviderId ? myProviderId : undefined) || '';
    const dur = asn?.expected_duration_minutes || 60;
    const code = asn?.auth_service_code || '92507';
    setPlanVisitForm({
      client_id: targetCid,
      provider_id: provId,
      dos: new Date().toISOString().slice(0, 10),
      start_time: '10:00',
      duration_minutes: dur,
      service_code: code,
      completion_notes: 'Planned therapy visit.'
    });
    setShowPlanVisitModal(true);
  };

  const handleSavePlanVisit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (planVisitSyncWarning) return;
    if (!planVisitForm.client_id || !planVisitForm.dos) return;
    const targetClient = clients.find(c => c.id === planVisitForm.client_id);
    if (targetClient && (targetClient.status || 'ACTIVE').toUpperCase() !== 'ACTIVE') {
      alert('Only active eligible clients may be planned.');
      return;
    }
    try {
      const selectedProv = planVisitForm.provider_id || (assignments.find(a => a.client_id === planVisitForm.client_id)?.provider_id);
      if (!selectedProv) {
        alert('Select a treating clinician. This account is not linked to a provider.');
        return;
      }
      const res = await fetch('/api/services', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: planVisitForm.client_id,
          provider_id: selectedProv,
          dos: planVisitForm.dos,
          start_time: planVisitForm.start_time || undefined,
          duration_minutes: Number(planVisitForm.duration_minutes) || 60,
          planned_duration_minutes: Number(planVisitForm.duration_minutes) || 60,
          service_code: planVisitForm.service_code || '92507',
          status: 'SCHEDULED',
          completion_notes: planVisitForm.completion_notes || 'Planned therapy visit.',
          id: planVisitSavedId || undefined,
          service_id: planVisitSavedId || undefined
        })
      });
      const data = await res.json().catch(() => ({}));
      const sync = readDisplaySync(data);
      if (sync.displayOutOfSync) {
        if (sync.serviceId) setPlanVisitSavedId(sync.serviceId);
        setPlanVisitSyncWarning(sync.warning);
        return;
      }
      if (res.ok) {
        setPlanVisitSavedId(null);
        setPlanVisitSyncWarning(null);
        setShowPlanVisitModal(false);
        await fetchData();
        if (onRefreshStats) onRefreshStats();
      }
    } catch (err) {
      console.error('Error planning visit:', err);
    }
  };

  const handleDeleteScheduledVisit = async (serviceId: string) => {
    if (!window.confirm('Are you sure you want to remove this planned visit?')) return;
    try {
      const res = await fetch(`/api/services/${serviceId}`, { method: 'DELETE' });
      if (res.ok) {
        await fetchData();
        if (onRefreshStats) onRefreshStats();
      }
    } catch (err) {
      console.error('Error removing planned visit:', err);
    }
  };

  const handleRecordCompletedService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (recordServiceSyncWarning) return;
    if (!recordServiceForm.client_id || !recordServiceForm.provider_id || !recordServiceForm.dos) return;
    try {
      const res = await fetch('/api/services', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...recordServiceForm,
          duration_minutes: recordServiceForm.actual_duration_minutes,
          status: 'COMPLETED',
          id: recordServiceSavedId || undefined,
          service_id: recordServiceSavedId || undefined
        })
      });
      const data = await res.json().catch(() => ({}));
      const sync = readDisplaySync(data);
      if (sync.displayOutOfSync) {
        if (sync.serviceId) setRecordServiceSavedId(sync.serviceId);
        setRecordServiceSyncWarning(sync.warning);
        return;
      }
      if (res.ok) {
        setRecordServiceSavedId(null);
        setRecordServiceSyncWarning(null);
        setShowRecordServiceModal(false);
        await fetchData();
        if (onRefreshStats) onRefreshStats();
      }
    } catch (err) {
      console.error('Error recording completed service:', err);
    }
  };

  // Canonical provider identity
  const myProvider = useMemo(() => {
    return providers.find(p =>
      (user?.providerId && p.id === user.providerId) ||
      (user?.id && p.user_id === user.id) ||
      (user?.email && (p as any).email?.toLowerCase() === user.email.toLowerCase())
    );
  }, [providers, user]);
  const myProviderId = user?.providerId || myProvider?.id;
  const myProviderName = user?.providerName || myProvider?.name;

  // Supervised assistants (SLPAs under this supervisor)
  const supervisedAssistants = useMemo(() => {
    if (!isSupervisor) return [];
    const slpas = providers.filter(p => p.role === 'SLPA' || p.role === 'ASSISTANT');
    const linked = slpas.filter(p =>
      (p.supervisor_id && myProviderId && p.supervisor_id === myProviderId) ||
      (myProviderId && assignments.some(a => a.provider_id === p.id && a.supervisor_id === myProviderId))
    );
    return linked;
  }, [isSupervisor, providers, myProviderId, assignments]);

  const supervisedAssistantIds = useMemo(() => supervisedAssistants.map(a => a.id), [supervisedAssistants]);

  // Assistant active clients
  const getAssistantActiveClients = (assistantId: string) => {
    return assignments.filter(a =>
      a.provider_id === assistantId &&
      (!a.client_status || a.client_status.toUpperCase() === 'ACTIVE') &&
      (a.active === 1 || a.active === true || a.active === undefined)
    );
  };

  // Direct active clients for supervisor
  const directActiveClients = useMemo(() => {
    if (!isSupervisor) return [];
    return assignments.filter(a =>
      a.provider_id === myProviderId &&
      (!a.client_status || a.client_status.toUpperCase() === 'ACTIVE') &&
      (a.active === 1 || a.active === true || a.active === undefined)
    );
  }, [isSupervisor, assignments, myProviderId]);

  const totalSupervisedClientsCount = useMemo(() => {
    const fromAssistants = supervisedAssistants.reduce((acc, ast) => acc + getAssistantActiveClients(ast.id).length, 0);
    return fromAssistants + directActiveClients.length;
  }, [supervisedAssistants, assignments, directActiveClients.length]);

  // Groupings
  const supervisors = providers.filter(p => p.role === 'SUPERVISOR' || p.role === 'SLP');
  const assistants = providers.filter(p => p.role === 'SLPA');

  const filteredAssignments = assignments.filter(a => {
    // Caseload integrity: Active Caseload must only include active clients
    if (a.client_status && a.client_status.toUpperCase() !== 'ACTIVE') return false;
    const matchesProvider = selectedProviderFilter === 'ALL' || a.provider_id === selectedProviderFilter;
    const matchesSearch = !searchQuery || 
      `${a.client_first_name} ${a.client_last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.ahcccs_id && a.ahcccs_id.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (a.provider_name && a.provider_name.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesProvider && matchesSearch;
  });

  // Completed services preserve full historical data (including inactive/discharged clients)
  const completedServices = useMemo(() => {
    return services.filter(s => {
      if (s.status !== 'COMPLETED') return false;
      if (isClinicianOrAssistant) {
        return myProviderId ? s.provider_id === myProviderId : true;
      }
      if (isSupervisor) {
        if (!myProviderId) return true;
        return s.provider_id === myProviderId || supervisedAssistantIds.includes(s.provider_id);
      }
      return true;
    });
  }, [services, isClinicianOrAssistant, isSupervisor, myProviderId, supervisedAssistantIds]);

  // Canonical Map of clinical documents by service_id and id
  const docsByServiceOrId = useMemo(() => {
    const map = new Map<string, ClinicalDocument>();
    clinicalDocs.forEach(d => {
      if (d.service_id) map.set(d.service_id, d);
      if (d.id) map.set(d.id, d);
    });
    return map;
  }, [clinicalDocs]);

  // Operational Visit Log services filtered by canonical provider + pay period + search
  const filteredCompletedServices = useMemo(() => {
    return completedServices.filter(s => {
      // 1. Provider Filter (stable providers.id)
      if (selectedVisitLogProviderId !== 'ALL') {
        if (s.provider_id !== selectedVisitLogProviderId) {
          return false;
        }
      }

      // 2. Pay Period Filter (canonical Allegiant biweekly cycle)
      if (selectedPayPeriod !== 'ALL') {
        if (!isDateInPayPeriod(s.dos, selectedPayPeriod)) {
          return false;
        }
      }

      // 3. Search query composition
      if (visitLogSearchQuery.trim()) {
        const q = visitLogSearchQuery.toLowerCase();
        const clientName = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
        const provName = (s.provider_name || '').toLowerCase();
        const cpt = (s.service_code || '').toLowerCase();
        const notes = (s.completion_notes || '').toLowerCase();
        const dos = (s.dos || '').toLowerCase();
        if (!clientName.includes(q) && !provName.includes(q) && !cpt.includes(q) && !notes.includes(q) && !dos.includes(q)) {
          return false;
        }
      }

      return true;
    });
  }, [completedServices, selectedVisitLogProviderId, selectedPayPeriod, visitLogSearchQuery]);

  // Providers list for the Visit Log selector (scoped cleanly by role)
  const visitLogProviders = useMemo(() => {
    let list = providers;
    if (isAssistant && myProviderId) {
      list = providers.filter(p => p.id === myProviderId);
    } else if (isSupervisor && myProviderId) {
      list = providers.filter(p => p.id === myProviderId || supervisedAssistantIds.includes(p.id));
    }
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [providers, isAssistant, isSupervisor, myProviderId, supervisedAssistantIds]);

  const selectedVisitLogProvider = useMemo(() => {
    if (selectedVisitLogProviderId === 'ALL') return null;
    return providers.find(p => p.id === selectedVisitLogProviderId) || null;
  }, [providers, selectedVisitLogProviderId]);

  const searchableVisitLogProviders = useMemo(() => {
    if (!providerSearchTerm.trim()) return visitLogProviders;
    const term = providerSearchTerm.toLowerCase();
    return visitLogProviders.filter(p =>
      p.name.toLowerCase().includes(term) ||
      (p.role && p.role.toLowerCase().includes(term)) ||
      (p.title && p.title.toLowerCase().includes(term)) ||
      p.id.toLowerCase().includes(term)
    );
  }, [visitLogProviders, providerSearchTerm]);

  const recentPayPeriods = useMemo(() => {
    return getRecentPayPeriods(4, 2);
  }, []);

  const handlePrevPeriod = () => {
    setSelectedPayPeriod(prev => prev === 'ALL' ? getAdjacentPayPeriod(getCurrentPayPeriod(), -1) : getAdjacentPayPeriod(prev, -1));
  };

  const handleNextPeriod = () => {
    setSelectedPayPeriod(prev => prev === 'ALL' ? getAdjacentPayPeriod(getCurrentPayPeriod(), 1) : getAdjacentPayPeriod(prev, 1));
  };

  // Visit Planner (upcoming scheduled visits) only contains active clients
  const scheduledServices = useMemo(() => {
    return services.filter(s => {
      if (s.status !== 'SCHEDULED') return false;
      const cli = clients.find(c => c.id === s.client_id);
      if (cli && (cli.status || 'ACTIVE').toUpperCase() !== 'ACTIVE') return false;
      if (isClinicianOrAssistant) {
        return myProviderId ? s.provider_id === myProviderId : true;
      }
      if (isSupervisor) {
        if (!myProviderId) return true;
        return s.provider_id === myProviderId || supervisedAssistantIds.includes(s.provider_id);
      }
      return true;
    });
  }, [services, clients, isClinicianOrAssistant, isSupervisor, myProviderId, supervisedAssistantIds]);

  // Eligible active clients for visit planning
  const eligibleActiveClients = useMemo(() => {
    const active = clients.filter(c => (c.status || 'ACTIVE').toUpperCase() === 'ACTIVE');
    if (isClinicianOrAssistant && myProviderId) {
      return active.filter(c => assignments.some(a => a.client_id === c.id && a.provider_id === myProviderId));
    }
    if (isSupervisor && myProviderId) {
      return active.filter(c => assignments.some(a =>
        a.client_id === c.id && (a.provider_id === myProviderId || a.supervisor_id === myProviderId || supervisedAssistantIds.includes(a.provider_id))
      ));
    }
    return active;
  }, [clients, isClinicianOrAssistant, isSupervisor, myProviderId, assignments, supervisedAssistantIds]);

  const availableTabs = useMemo(() => {
    if (isAssistant) {
      return [
        {
          key: 'CALENDAR' as const,
          label: 'Upcoming Visits',
          icon: Calendar,
          count: scheduledServices.length
        },
        {
          key: 'COMPLETED_SERVICES' as const,
          label: 'Visit Log',
          icon: CheckCircle,
          count: filteredCompletedServices.length
        }
      ];
    }
    if (isSupervisor) {
      return [
        {
          key: 'RELATIONSHIPS' as const,
          label: 'Caseload',
          icon: UserCheck,
          count: totalSupervisedClientsCount
        },
        {
          key: 'CALENDAR' as const,
          label: 'Upcoming Visits',
          icon: Calendar,
          count: scheduledServices.length
        },
        {
          key: 'COMPLETED_SERVICES' as const,
          label: 'Visit Log',
          icon: CheckCircle,
          count: filteredCompletedServices.length
        }
      ];
    }
    return [
      {
        key: 'RELATIONSHIPS' as const,
        label: 'Caseload',
        icon: UserCheck,
        count: assignments.filter(a => !a.client_status || a.client_status.toUpperCase() === 'ACTIVE').length
      },
      {
        key: 'CALENDAR' as const,
        label: 'Upcoming Visits',
        icon: Calendar,
        count: scheduledServices.length
      },
      {
        key: 'COMPLETED_SERVICES' as const,
        label: 'Visit Log',
        icon: CheckCircle,
        count: filteredCompletedServices.length
      }
    ];
  }, [isAssistant, isSupervisor, scheduledServices.length, filteredCompletedServices.length, totalSupervisedClientsCount, assignments]);

  const formatSessionTime = (start?: string, end?: string): string => {
    if (!start) return '—';
    const formatSingle = (t: string) => {
      if (!t) return '';
      if (t.includes('AM') || t.includes('PM')) return t;
      const parts = t.split(':');
      if (parts.length >= 2) {
        let h = parseInt(parts[0], 10);
        const m = parts[1];
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h}:${m} ${ampm}`;
      }
      return t;
    };
    const startFmt = formatSingle(start);
    const endFmt = end ? formatSingle(end) : '';
    return endFmt ? `${startFmt} – ${endFmt}` : startFmt;
  };

  const resolveSessionSignatures = (
    service: any,
    doc?: ClinicalDocument,
    provider?: Provider
  ) => {
    const isAssistantProv = provider?.role === 'SLPA' || service.provider_role === 'SLPA';

    // Clinician signature
    const clinicianSigned = Boolean(
      doc?.clinician_signature ||
      doc?.clinician_signed_at ||
      service.clinician_signature ||
      service.clinician_signed_at ||
      doc?.status === 'FINALIZED'
    );
    const clinician = clinicianSigned
      ? {
          status: 'SIGNED' as const,
          label: '✓ Signed',
          badgeClass: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
          signer: doc?.clinician_signature || service.clinician_signature
        }
      : {
          status: 'PENDING' as const,
          label: 'Pending',
          badgeClass: 'bg-amber-500/15 text-amber-300 border-amber-500/30'
        };

    // Guardian signature
    const guardianStatus = (doc?.guardian_signature_status || service.guardian_signature_status || '').toUpperCase();
    const guardianSigned = Boolean(
      doc?.guardian_signature ||
      doc?.guardian_signed_at ||
      service.guardian_signature ||
      service.guardian_signed_at ||
      guardianStatus === 'SIGNED'
    );
    const isWaived = guardianStatus === 'WAIVED_DOCUMENTED';
    const guardianWindow = assessGuardianPayWindow({
      dos: service.dos || doc?.dos,
      end_time: service.end_time || (doc as any)?.end_time,
      guardian_signed_at: doc?.guardian_signed_at || service.guardian_signed_at,
      esign_completed_at: (doc as any)?.esign_completed_at,
      guardian_signature_status: guardianStatus
    });
    const isOverdue = !guardianSigned && !isWaived && guardianWindow.isLate;

    let guardian;
    if (isWaived) {
      guardian = {
        status: 'WAIVED' as const,
        label: 'Waived',
        badgeClass: 'bg-sky-500/15 text-sky-300 border-sky-500/30'
      };
    } else if (guardianSigned) {
      guardian = {
        status: 'SIGNED' as const,
        label: '✓ Signed',
        badgeClass: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
        signer: guardianWindow.isLate
          ? `${doc?.guardian_signature || service.guardian_signature || 'Guardian'}. ${GUARDIAN_OVERDUE_PAY_MESSAGE}`
          : doc?.guardian_signature || service.guardian_signature
      };
    } else if (guardianWindow.missing) {
      guardian = {
        status: 'PENDING' as const,
        label: 'End time needed',
        badgeClass: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
        signer: GUARDIAN_CLOCK_MISSING_MESSAGE
      };
    } else if (isOverdue) {
      guardian = {
        status: 'OVERDUE' as const,
        label: GUARDIAN_OVERDUE_PAY_MESSAGE,
        badgeClass: 'bg-rose-500/15 text-rose-300 border-rose-500/30'
      };
    } else {
      guardian = {
        status: 'PENDING' as const,
        label: guardianWindow.deadlineLabel ? `Due by ${guardianWindow.deadlineLabel}` : 'Pending',
        badgeClass: 'bg-amber-500/15 text-amber-300 border-amber-500/30'
      };
    }

    // Supervisor signature
    const requiresSupervisor = isAssistantProv || Boolean(doc?.supervisor_id);
    let supervisor;
    if (!requiresSupervisor) {
      supervisor = {
        status: 'NOT_REQUIRED' as const,
        label: 'Not Required',
        badgeClass: 'bg-slate-800 text-slate-400 border-slate-700/60'
      };
    } else {
      const supervisorSigned = Boolean(
        doc?.supervisor_signature ||
        doc?.supervisor_signed_at ||
        service.supervisor_signature ||
        service.supervisor_signed_at ||
        doc?.supervisor_approval_status === 'APPROVED'
      );

      if (supervisorSigned) {
        supervisor = {
          status: 'SIGNED' as const,
          label: '✓ Signed',
          badgeClass: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
          signer: doc?.supervisor_signature || service.supervisor_signature
        };
      } else {
        supervisor = {
          status: 'PENDING' as const,
          label: 'Pending',
          badgeClass: 'bg-amber-500/15 text-amber-300 border-amber-500/30'
        };
      }
    }

    return { guardian, supervisor, clinician };
  };

  const resolveBillingReadiness = (service: any) => {
    const rawStatus = (service.readiness_status || '').toUpperCase();
    const holdReasons: string[] = Array.isArray(service.hold_reasons) ? service.hold_reasons : [];

    if (rawStatus === 'BILLING_READY' || rawStatus === 'READY') {
      return {
        status: 'READY' as const,
        label: 'READY',
        badgeClass: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
        holdReasons: []
      };
    }

    if (rawStatus.startsWith('HOLD') || rawStatus === 'HELD' || holdReasons.length > 0) {
      return {
        status: 'HELD' as const,
        label: 'HELD',
        badgeClass: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
        holdReasons
      };
    }

    return {
      status: 'NOT_READY' as const,
      label: 'NOT READY',
      badgeClass: 'bg-slate-800 text-slate-400 border-slate-700/60',
      holdReasons
    };
  };

  return (
    <div className="space-y-3 pb-8">
      {/* ROW 1 — PRIMARY WORKSPACE HEADER: Wrapped 2-row on phone, single row on sm+ */}
      {/* ROW 1 — PRIMARY WORKSPACE HEADER: Wrapped 2-row on phone, single row on sm+ */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-2 border-b border-slate-800/80 w-full max-w-full">
        {/* Left: Caseload / Upcoming Visits / Visit Log + Plan Visit on Supervisor desktop */}
        <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap sm:flex-nowrap w-full sm:w-auto">
          {/* Row 1 on mobile: Caseload / Upcoming Visits / Visit Log (dynamic 2 or 3 cols, zero sideways scroll) */}
          <div className={`grid ${availableTabs.length === 2 ? 'grid-cols-2' : 'grid-cols-3'} sm:flex sm:items-center gap-1 sm:space-x-1.5 p-1 rounded-xl pulse-control-shell w-full sm:w-auto`}>
            {availableTabs.map((tab) => {
              const isSelected = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`relative px-1 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold flex items-center justify-center sm:justify-start transition-all cursor-pointer overflow-hidden min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                    isSelected
                      ? 'bg-slate-800 text-white border border-slate-700 font-semibold shadow-xs'
                      : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                  }`}
                >
                  {/* Mobile: 2-tier compact layout (Icon + Badge top, full label bottom) */}
                  <div className="flex sm:hidden flex-col items-center justify-center py-0.5 gap-0.5 w-full min-w-0">
                    <div className="flex items-center justify-center gap-1.5 shrink-0">
                      <tab.icon className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-white' : 'text-slate-600'}`} />
                      <span className={`text-[10px] px-1.5 py-0.2 rounded font-mono font-bold leading-none shrink-0 ${
                        isSelected
                          ? 'bg-white/15 text-white border border-white/20'
                          : 'bg-slate-100 text-slate-700 border border-slate-200'
                      }`}>
                        {tab.count}
                      </span>
                    </div>
                    <span className="text-[10.5px] font-semibold leading-tight text-center tracking-tight">
                      {tab.label}
                    </span>
                  </div>

                  {/* Desktop / Tablet: Exact hard-frozen single-row layout (Icon, Label, Badge) */}
                  <div className="hidden sm:flex sm:items-center sm:space-x-2">
                    <tab.icon className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-white' : 'text-slate-600'}`} />
                    <span>{tab.label}</span>
                    <span className={`text-[10px] px-1.5 py-0.2 rounded font-mono font-bold leading-none shrink-0 ${
                      isSelected
                        ? 'bg-white/15 text-white border border-white/20'
                        : 'bg-slate-100 text-slate-700 border border-slate-200'
                    }`}>
                      {tab.count}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Supervisor & Assistant Desktop: Plan Visit sits immediately to the right of Caseload / Upcoming Visits / Visit Log */}
          {(isSupervisor || isAssistant) && (
            <button
              onClick={() => openPlanVisitModal()}
              className="hidden sm:flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 text-xs font-semibold shrink-0 transition-colors shadow-xs cursor-pointer active:scale-98"
            >
              <Calendar className="w-3.5 h-3.5 text-slate-300" />
              <span>Plan Visit</span>
            </button>
          )}
        </div>

        {/* Row 2 on mobile / Right on sm+: Plan Visit + Actions (clean phone reflow, comfortable touch target) */}
        <div className={`flex items-center space-x-1.5 sm:space-x-2 shrink-0 ${isAssistant ? 'w-full sm:hidden py-0.5' : isOperationalRole ? 'w-full sm:w-auto py-0.5' : (isSupervisor || isAssistant) ? 'hidden' : 'hidden sm:flex'}`}>
          {!isSupervisor && !isAssistant && (
            <button
              onClick={() => openPlanVisitModal()}
              className="hidden sm:flex sm:flex-initial text-white bg-slate-800 hover:bg-slate-700 border border-slate-700 shadow-xs justify-center px-3 py-2 sm:py-1.5 rounded-lg font-semibold text-xs items-center space-x-1.5 shrink-0 transition-all active:scale-98 cursor-pointer"
            >
              <Calendar className="w-3.5 h-3.5 text-slate-300" />
              <span>Plan Visit</span>
            </button>
          )}
          {isAssistant && (
            <button
              onClick={() => openPlanVisitModal()}
              className="sm:hidden flex flex-1 justify-center px-3 py-2 rounded-lg font-semibold text-xs items-center space-x-1.5 shrink-0 transition-all active:scale-98 cursor-pointer text-white bg-slate-800 hover:bg-slate-700 border border-slate-700 shadow-xs"
            >
              <Calendar className="w-3.5 h-3.5 text-slate-300" />
              <span>Plan Visit</span>
            </button>
          )}
          {isOperationalRole && (
            <button
              onClick={() => setShowAssignModal(true)}
              className="flex-1 sm:flex-initial justify-center px-2.5 py-2 sm:py-1.5 rounded-lg bg-gradient-to-b from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-slate-200 hover:text-white font-semibold text-xs flex items-center space-x-1.5 border border-slate-700/80 hover:border-slate-600 shadow-[0_2px_6px_rgba(0,0,0,0.3)] transition-all active:scale-98 cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5 text-slate-400" />
              <span>Assign Client</span>
            </button>
          )}
          {(userRole === 'ADMIN' || userRole === 'OFFICE') && (
            <button
              onClick={() => setShowAddTherapistModal(true)}
              className="flex-1 sm:flex-initial justify-center px-2.5 py-2 sm:py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs flex items-center space-x-1.5 shadow-sm transition-all active:scale-98 cursor-pointer"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>Add Therapist</span>
            </button>
          )}
        </div>
      </div>

      {/* OWNER LIVE SESSIONS OPERATIONAL SURFACE (COMPACT BOSS GLANCE VIEW) */}
      {(userRole === 'OWNER' || userRole === 'ADMIN') && liveSessions.length > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-b from-[#101b2e] via-[#0b1322] to-[#070c16] rounded-xl border border-[#2b3a52] shadow-[0_4px_16px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.06)] p-2.5 space-y-2">
          {/* Specular top hairline */}
          <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent pointer-events-none" />

          {/* Header row */}
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
              <span className="text-[11px] font-bold text-slate-200 tracking-wider uppercase">
                Live Encounters
              </span>
              <span className="px-1.5 py-0.2 rounded font-mono text-[10px] font-semibold bg-emerald-950/80 text-emerald-300 border border-emerald-500/30">
                {liveSessions.length} active
              </span>
            </div>
            <span className="text-[10px] text-slate-400">Observability Glance</span>
          </div>

          {/* Rows: green subtle beacon • Provider • Client • elapsed • CPT • Exception chip */}
          <div className="space-y-1.5">
            {liveSessions.map((session) => {
              const elapsedMins = Math.max(0, Math.floor((Date.now() - session.startTimestamp) / 60000));
              return (
                <div
                  key={`${session.providerId}::${session.clientId}`}
                  onClick={() => onSelectClient(session.clientId)}
                  role="button"
                  tabIndex={0}
                  className="group relative px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-all duration-150 flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 cursor-pointer select-none shadow-xs"
                  title="Open client chart"
                >
                  <div className="flex items-center space-x-2.5 min-w-0 flex-wrap">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                    <span className="text-xs font-semibold text-slate-800 truncate">
                      {session.providerName}
                    </span>
                    <span className="text-slate-400 text-[11px]">•</span>
                    <span className="text-xs font-medium text-slate-700 truncate">
                      {session.clientName}
                    </span>
                    <span className="text-slate-400 text-[11px]">•</span>
                    <span className="font-mono text-[11px] text-slate-400">
                      {elapsedMins}m elapsed
                    </span>
                    <span className="text-slate-500 text-[11px]">•</span>
                    <span className="font-mono text-[11px] font-semibold text-sky-800 bg-sky-50 px-1.5 py-0.2 rounded border border-sky-200">
                      CPT {session.serviceCode}
                    </span>
                  </div>

                  {session.authException && (
                    <div className="shrink-0 flex items-center">
                      {session.authException === 'No Active Auth' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                          No Active Auth
                        </span>
                      )}
                      {session.authException === 'Makeup' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-800 border border-purple-200">
                          Makeup
                        </span>
                      )}
                      {session.authException !== 'No Active Auth' && session.authException !== 'Makeup' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-800 border border-slate-200">
                          {session.authException}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Success Notification Banner */}
      {therapistSuccessBanner && (
        <div className="p-2.5 bg-emerald-950/40 border border-emerald-800 text-emerald-300 rounded-xl text-xs flex items-center justify-between shadow-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{therapistSuccessBanner}</span>
          </div>
          <button
            onClick={() => setTherapistSuccessBanner(null)}
            className="text-slate-400 hover:text-white p-1 rounded hover:bg-white/5"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* TAB 1: CASELOAD & SUPERVISOR RELATIONSHIPS (CORE CENTER) */}
      {activeTab === 'RELATIONSHIPS' && (
        <div className="space-y-3">
          {isSupervisor ? (
            /* SUPERVISOR CASELOAD VIEW: COLLAPSIBLE ASSISTANT -> ACTIVE CLIENT HIERARCHY */
            <div className="space-y-3">
              {/* Filter / Search Bar - Tightened */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 clinical-structural-header px-3 py-1.5 rounded-xl border border-slate-700/80 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-sky-400/25 to-transparent pointer-events-none" />
                {/* Mobile Section Identity (sm:hidden) — Centered Title with Centered Summary directly beneath */}
                <div className="sm:hidden flex flex-col items-center justify-center text-center space-y-1 w-full pt-1 pb-0.5">
                  <div className="text-xs font-bold text-white tracking-tight flex items-center justify-center space-x-1.5">
                    <Shield className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                    <span>Supervised Assistant Caseloads</span>
                  </div>
                  <div className="inline-flex items-center justify-center space-x-1.5 text-[10px] font-mono">
                    <span className="text-sky-300 font-bold">{supervisedAssistants.length}</span>
                    <span className="text-slate-400">Assistant{supervisedAssistants.length === 1 ? '' : 's'}</span>
                    <span className="text-slate-500">•</span>
                    <span className="text-emerald-300 font-bold">{totalSupervisedClientsCount}</span>
                    <span className="text-slate-400">active clients</span>
                  </div>
                </div>

                {/* Desktop/Tablet Section Identity (hidden sm:flex) - HARD FROZEN */}
                <div className="hidden sm:flex items-center space-x-2 flex-wrap gap-y-1">
                  <div className="text-xs font-bold text-white tracking-tight flex items-center space-x-1.5">
                    <Shield className="w-3.5 h-3.5 text-indigo-400" />
                    <span>Supervised Assistant Caseloads</span>
                  </div>
                  <div className="inline-flex items-center space-x-1.5 text-[10px] font-mono">
                    <span className="text-sky-300 font-bold">{supervisedAssistants.length}</span>
                    <span className="text-slate-400">Assistant{supervisedAssistants.length === 1 ? '' : 's'}</span>
                    <span className="text-slate-500">•</span>
                    <span className="text-emerald-300 font-bold">{totalSupervisedClientsCount}</span>
                    <span className="text-slate-400">active clients</span>
                  </div>
                </div>
                <div className="w-full sm:w-60 shrink-0">
                  <ArcSearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search caseload..."
                    className="w-full text-xs"
                    variant="light"
                  />
                </div>
              </div>

              {/* Supervised Assistant Cards */}
              <div className="space-y-2.5">
                {supervisedAssistants.length === 0 ? (
                  <div className="glass-panel p-6 text-center text-slate-400 text-xs rounded-xl border border-slate-800">
                    No assistants currently assigned under your direct supervision.
                  </div>
                ) : (
                  supervisedAssistants.map((assistant) => {
                    const activeClients = getAssistantActiveClients(assistant.id);
                    const isCollapsed = !!collapsedAssistants[assistant.id];
                    const q = searchQuery.toLowerCase().trim();
                    const assistantMatches = q && assistant.name.toLowerCase().includes(q);
                    const visibleClients = q && !assistantMatches
                      ? activeClients.filter((c) =>
                          `${c.client_first_name} ${c.client_last_name}`.toLowerCase().includes(q) ||
                          `${c.client_last_name}, ${c.client_first_name}`.toLowerCase().includes(q) ||
                          (c.ahcccs_id && c.ahcccs_id.toLowerCase().includes(q))
                        )
                      : activeClients;

                    if (q && !assistantMatches && visibleClients.length === 0) {
                      return null;
                    }

                    return (
                      <div
                        key={assistant.id}
                        className="rounded-xl border border-slate-700/80 md:border-slate-800/90 mobile-ops-shell md:bg-gradient-to-b md:from-slate-950/70 md:to-[#070b13]/90 overflow-hidden shadow-sm hover:border-slate-700/80 transition-all"
                      >
                        {/* Assistant Header Row */}
                        <div
                          onClick={() => toggleAssistantCollapse(assistant.id)}
                          className="px-3 py-2 mobile-ops-header md:bg-gradient-to-r md:from-slate-900/95 md:via-[#0d1627]/95 md:to-slate-950/95 hover:from-slate-900 hover:to-slate-900/90 cursor-pointer border-b border-slate-700/70 md:border-slate-800/80 flex items-center justify-between transition-all select-none relative overflow-hidden group"
                        >
                          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-sky-400/25 to-transparent pointer-events-none" />
                          <div className="flex items-center space-x-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-md bg-sky-500/15 border border-sky-500/30 flex items-center justify-center font-bold text-sky-300 text-[11px] shrink-0 shadow-xs">
                              {assistant.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center space-x-1.5 flex-wrap">
                                <span className="text-xs font-bold text-white tracking-tight truncate">
                                  {assistant.name}
                                </span>
                                <span className="text-[9px] font-mono font-semibold px-1.5 py-0.2 rounded bg-transparent text-sky-200 border border-sky-300/35 leading-none">
                                  {assistant.role || 'SLPA'}
                                </span>
                                {liveSessions.some(s => s.providerId === assistant.id) && (
                                  <span className="inline-flex items-center space-x-1.5 px-2 py-0.5 rounded-full bg-emerald-950/80 border border-emerald-500/30 text-[10px] font-semibold text-emerald-300">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                                    <span>IN SESSION</span>
                                  </span>
                                )}
                                {assistant.license_number && (
                                  <span className="hidden sm:inline text-[9px] font-mono text-slate-500">
                                    Lic: {assistant.license_number}
                                  </span>
                                )}
                              </div>
                              <div className="hidden sm:block text-[10px] text-slate-300 leading-tight truncate">
                                Supervised Practice • Speech-Language Pathology Assistant
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 shrink-0">
                            <span className="text-[10.5px] font-semibold font-mono text-slate-300">
                              {activeClients.length} active client{activeClients.length === 1 ? '' : 's'}
                            </span>
                            <div className="p-0.5 rounded text-slate-400 group-hover:text-white transition-colors">
                              <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${!isCollapsed ? 'rotate-90 text-sky-400' : 'text-slate-500'}`} />
                            </div>
                          </div>
                        </div>

                        {/* Collapsible Active Clients - Flattened Records with Hairline Separators */}
                        {!isCollapsed && (
                          <div className="divide-y divide-slate-700/60 md:divide-slate-700/60 bg-transparent">
                            {visibleClients.length === 0 ? (
                              <div className="py-3 px-3 text-center text-slate-400 text-xs italic">
                                {q ? 'No clients match your search under this assistant.' : 'No active clients currently assigned to this assistant.'}
                              </div>
                            ) : (
                              visibleClients.map((client, idx) => {
                                const freq = client.expected_frequency || client.auth_frequency || '2x/week';
                                const duration = client.expected_duration_minutes || 60;
                                return (
                                  <div
                                    key={client.id}
                                    tabIndex={0}
                                    role="button"
                                    onClick={() => onSelectClient(client.client_id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onSelectClient(client.client_id);
                                      }
                                    }}
                                    className={`group relative py-2 sm:py-1.5 px-3 flex items-center justify-between gap-2 ${
                                      idx % 2 === 0
                                        ? 'ops-row-a'
                                        : 'ops-row-b'
                                    } hover:bg-[#20314a] border-l-2 border-l-indigo-500/40 md:border-l-transparent hover:border-l-cyan-400/80 cursor-pointer select-none transition-colors duration-150 focus:outline-none`}
                                  >
                                    {/* Subtle interactive sheen sweep on hover (pure light, no movement) */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/[0.04] via-sky-400/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />

                                    <div className="flex items-center space-x-2.5 min-w-0">
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 shadow-[0_0_6px_rgba(52,211,153,0.6)]" />
                                      <div className="min-w-0">
                                        <div className="flex items-center space-x-1.5 flex-wrap">
                                          <span className="font-semibold text-slate-100 text-xs truncate group-hover:text-cyan-200 transition-colors">
                                            {client.client_last_name}, {client.client_first_name}
                                          </span>
                                          {liveSessions.some(s => s.clientId === client.client_id || s.clientId === client.id) && (
                                            <span className="inline-flex items-center space-x-1 px-1.5 py-0.2 rounded bg-emerald-950/80 border border-emerald-500/30 text-[9px] font-semibold text-emerald-300">
                                              <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                                              <span>IN SESSION</span>
                                            </span>
                                          )}
                                          <span className="hidden sm:inline-block text-[9px] font-mono font-semibold px-1.5 py-0.2 rounded bg-slate-100 border border-slate-200 text-slate-800 leading-none">
                                            {freq}
                                          </span>
                                          <span className="hidden sm:inline-block text-[9px] font-mono text-slate-700">
                                            {duration}m/session
                                          </span>
                                        </div>
                                        <div className="hidden sm:block text-[9.5px] font-mono text-slate-500 leading-tight mt-0.5">
                                          AHCCCS: <span className="text-slate-400">{client.ahcccs_id || client.client_id}</span> • CPT <span className="text-slate-400">{client.auth_service_code || '92507'}</span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex items-center space-x-1.5 shrink-0">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onSelectClient(client.client_id);
                                        }}
                                        className="px-2 py-0.5 text-[11px] font-semibold rounded-md bg-gradient-to-b from-slate-900/90 to-slate-950/90 hover:from-slate-800 hover:to-slate-900 text-sky-200 hover:text-white border border-sky-500/35 hover:border-sky-400/60 shadow-[0_1px_4px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.1)] hover:shadow-[0_0_8px_rgba(56,189,248,0.25)] transition-all cursor-pointer flex items-center space-x-1 active:scale-98"
                                      >
                                        <span>View Chart</span>
                                        <ChevronRight className="w-3 h-3 text-sky-400/80" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openPlanVisitModal(client.client_id, assistant.id);
                                        }}
                                        className="hidden sm:flex px-2 py-0.5 text-[11px] font-semibold rounded-md bg-gradient-to-r from-purple-950/80 to-indigo-950/80 hover:from-purple-900/90 hover:to-indigo-900/90 text-purple-200 hover:text-white border border-purple-500/45 hover:border-purple-400/70 shadow-[0_1px_4px_rgba(147,51,234,0.2),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_0_8px_rgba(168,85,247,0.35)] transition-all cursor-pointer items-center space-x-1 active:scale-98"
                                      >
                                        <Calendar className="w-3 h-3 text-purple-300" />
                                        <span>Plan Visit</span>
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                {/* Direct Supervisor Caseload (if any) */}
                {directActiveClients.length > 0 && (
                  <div className="rounded-xl border border-indigo-500/40 md:border-indigo-900/50 mobile-ops-shell md:bg-gradient-to-b md:from-slate-950/70 md:to-[#070b13]/90 overflow-hidden shadow-sm hover:border-indigo-800/60 transition-all mt-2.5">
                    <div
                      onClick={() => toggleAssistantCollapse('DIRECT_SUPERVISOR_CASELOAD')}
                      className="px-3 py-2 mobile-ops-header md:bg-gradient-to-r md:from-slate-900/95 md:via-[#131428]/95 md:to-slate-950/95 hover:from-slate-900 hover:to-slate-900/90 cursor-pointer border-b border-indigo-700/60 md:border-indigo-900/50 flex items-center justify-between transition-all select-none relative overflow-hidden group"
                    >
                      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-indigo-400/30 to-transparent pointer-events-none" />
                      <div className="flex items-center space-x-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-md bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center font-bold text-indigo-300 text-xs shrink-0 shadow-xs">
                          <UserCheck className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center space-x-1.5 flex-wrap">
                            <span className="text-xs font-bold text-white tracking-tight truncate">
                              Direct Supervisory Caseload ({myProviderName || 'Clinical Supervisor'})
                            </span>
                            <span className="text-[9px] font-mono font-semibold px-1.5 py-0.2 rounded bg-transparent text-indigo-200 border border-indigo-300/35 leading-none">
                              Direct Treating
                            </span>
                          </div>
                          <div className="hidden sm:block text-[10px] text-slate-300 leading-tight truncate">
                            Clients directly assigned to supervisor
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 shrink-0">
                        <span className="text-[10.5px] font-semibold font-mono text-indigo-200">
                          {directActiveClients.length} active client{directActiveClients.length === 1 ? '' : 's'}
                        </span>
                        <div className="p-0.5 rounded text-slate-400 group-hover:text-white transition-colors">
                          <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${!collapsedAssistants['DIRECT_SUPERVISOR_CASELOAD'] ? 'rotate-90 text-indigo-400' : 'text-slate-500'}`} />
                        </div>
                      </div>
                    </div>

                    {!collapsedAssistants['DIRECT_SUPERVISOR_CASELOAD'] && (
                      <div className="divide-y divide-slate-700/60 md:divide-indigo-900/60 bg-transparent">
                        {directActiveClients.map((client, idx) => {
                          const freq = client.expected_frequency || client.auth_frequency || '2x/week';
                          const duration = client.expected_duration_minutes || 60;
                          return (
                            <div
                              key={client.id}
                              tabIndex={0}
                              role="button"
                              onClick={() => onSelectClient(client.client_id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onSelectClient(client.client_id);
                                }
                              }}
                              className={`group relative py-2 sm:py-1.5 px-3 flex items-center justify-between gap-2 ${
                                idx % 2 === 0
                                  ? 'ops-row-a'
                                  : 'ops-row-b'
                              } hover:bg-[#20314a] border-l-2 border-l-purple-500/50 md:border-l-transparent hover:border-l-indigo-400/80 cursor-pointer select-none transition-colors duration-150 focus:outline-none`}
                            >
                              {/* Subtle interactive sheen sweep on hover (pure light, no movement) */}
                              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/[0.05] via-purple-400/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />

                              <div className="flex items-center space-x-2.5 min-w-0">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 shadow-[0_0_6px_rgba(52,211,153,0.6)]" />
                                <div className="min-w-0">
                                  <div className="flex items-center space-x-1.5 flex-wrap">
                                    <span className="font-semibold text-slate-100 text-xs truncate group-hover:text-indigo-200 transition-colors">
                                      {client.client_last_name}, {client.client_first_name}
                                    </span>
                                    {liveSessions.some(s => s.clientId === client.client_id || s.clientId === client.id) && (
                                      <span className="inline-flex items-center space-x-1 px-1.5 py-0.2 rounded bg-emerald-950/80 border border-emerald-500/30 text-[9px] font-semibold text-emerald-300">
                                        <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                                        <span>IN SESSION</span>
                                      </span>
                                    )}
                                    <span className="hidden sm:inline-block text-[9px] font-mono font-semibold px-1.5 py-0.2 rounded bg-slate-100 border border-slate-200 text-slate-800 leading-none">
                                      {freq}
                                    </span>
                                    <span className="hidden sm:inline-block text-[9px] font-mono text-slate-700">
                                      {duration}m/session
                                    </span>
                                  </div>
                                  <div className="hidden sm:block text-[9.5px] font-mono text-slate-500 leading-tight mt-0.5">
                                    AHCCCS: <span className="text-slate-400">{client.ahcccs_id || client.client_id}</span> • CPT <span className="text-slate-400">{client.auth_service_code || '92507'}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center space-x-1.5 shrink-0">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectClient(client.client_id);
                                  }}
                                  className="px-2 py-0.5 text-[11px] font-semibold rounded-md bg-gradient-to-b from-slate-900/90 to-slate-950/90 hover:from-slate-800 hover:to-slate-900 text-sky-200 hover:text-white border border-sky-500/35 hover:border-sky-400/60 shadow-[0_1px_4px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.1)] hover:shadow-[0_0_8px_rgba(56,189,248,0.25)] transition-all cursor-pointer flex items-center space-x-1 active:scale-98"
                                >
                                  <span>View Chart</span>
                                  <ChevronRight className="w-3 h-3 text-sky-400/80" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openPlanVisitModal(client.client_id, myProviderId);
                                  }}
                                  className="hidden sm:flex px-2 py-0.5 text-[11px] font-semibold rounded-md bg-gradient-to-r from-purple-950/80 to-indigo-950/80 hover:from-purple-900/90 hover:to-indigo-900/90 text-purple-200 hover:text-white border border-purple-500/45 hover:border-purple-400/70 shadow-[0_1px_4px_rgba(147,51,234,0.2),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_0_8px_rgba(168,85,247,0.35)] transition-all cursor-pointer items-center space-x-1 active:scale-98"
                                >
                                  <Calendar className="w-3 h-3 text-purple-300" />
                                  <span>Plan Visit</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* ROW 2 — VIEW MODE + SEARCH */}
              <div className="p-2 sm:p-2.5 rounded-xl border border-slate-700/80 md:border-slate-800/80 mobile-ops-shell md:bg-gradient-to-r md:from-[#0c1424]/90 md:via-[#080d18]/90 md:to-[#050810]/95 backdrop-blur-md shadow-[0_4px_16px_rgba(0,0,0,0.4)] flex flex-col gap-2">
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
                  <div className="flex flex-wrap sm:flex-nowrap items-center gap-1 p-1 rounded-xl pulse-control-shell w-full sm:w-auto">
                    <button
                      onClick={() => setRelationshipSubTab('CLIENT_ASSIGNMENTS')}
                      className={`flex-1 sm:flex-initial justify-center px-2 sm:px-2.5 py-1 rounded-lg text-[11px] sm:text-[11.5px] font-semibold tracking-tight transition-all cursor-pointer flex items-center space-x-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                        relationshipSubTab === 'CLIENT_ASSIGNMENTS'
                          ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      }`}
                    >
                      <span className={relationshipSubTab === 'CLIENT_ASSIGNMENTS' ? 'text-white' : 'text-slate-800'}>Client Assignments</span>
                      <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded font-bold leading-none ${
                        relationshipSubTab === 'CLIENT_ASSIGNMENTS' ? 'bg-white/15 text-white border border-white/20' : 'bg-slate-100 text-slate-700 border border-slate-200'
                      }`}>
                        {assignments.filter(a => !a.client_status || a.client_status.toUpperCase() === 'ACTIVE').length}
                      </span>
                    </button>
                    <button
                      onClick={() => setRelationshipSubTab('SUPERVISOR_OVERSIGHT')}
                      className={`flex-1 sm:flex-initial justify-center px-2 sm:px-2.5 py-1 rounded-lg text-[11px] sm:text-[11.5px] font-semibold tracking-tight transition-all cursor-pointer flex items-center space-x-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                        relationshipSubTab === 'SUPERVISOR_OVERSIGHT'
                          ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      }`}
                    >
                      <span className={relationshipSubTab === 'SUPERVISOR_OVERSIGHT' ? 'text-white' : 'text-slate-800'}>Supervision</span>
                      <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded font-bold leading-none ${
                        relationshipSubTab === 'SUPERVISOR_OVERSIGHT' ? 'bg-white/15 text-white border border-white/20' : 'bg-slate-100 text-slate-700 border border-slate-200'
                      }`}>
                        {assistants.length}
                      </span>
                    </button>
                    <button
                      onClick={() => setRelationshipSubTab('BY_PROVIDER')}
                      className={`flex-1 sm:flex-initial justify-center px-2 sm:px-2.5 py-1 rounded-lg text-[11px] sm:text-[11.5px] font-semibold tracking-tight transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                        relationshipSubTab === 'BY_PROVIDER'
                          ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      }`}
                    >
                      <span className={relationshipSubTab === 'BY_PROVIDER' ? 'text-white' : 'text-slate-800'}>By Provider</span>
                    </button>
                  </div>

                  {/* Quick Pulse Search */}
                  <div className="w-full sm:w-72 md:w-80 shrink-0">
                    <ArcSearchInput
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search clients..."
                      className="w-full"
                      variant="light"
                    />
                  </div>
                </div>

                {/* If By Provider is active, provider selector pills live in this same second-row area directly beneath */}
                {relationshipSubTab === 'BY_PROVIDER' && (
                  <div className="pt-2 border-t border-slate-800/70 flex flex-wrap items-center gap-1.5">
                    <button
                      onClick={() => setSelectedProviderFilter('ALL')}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer flex items-center space-x-1.5 ${
                        selectedProviderFilter === 'ALL'
                          ? 'bg-gradient-to-b from-sky-600 via-sky-700 to-blue-800 text-white shadow-[0_1px_6px_rgba(2,132,199,0.3),inset_0_1px_0_rgba(255,255,255,0.2)] border border-sky-400/40'
                          : 'bg-slate-900/90 text-slate-300 hover:text-white hover:bg-slate-800 border border-slate-800/90 shadow-xs'
                      }`}
                    >
                      <span>All Providers</span>
                      <span className={`text-[9.5px] font-mono px-1 py-0.2 rounded font-bold leading-none ${
                        selectedProviderFilter === 'ALL' ? 'bg-white/20 text-white' : 'bg-slate-950 text-white'
                      }`}>
                        {filteredAssignments.length}
                      </span>
                    </button>
                    {providers.map((p) => {
                      const pActiveCount = assignments.filter(a => (!a.client_status || a.client_status.toUpperCase() === 'ACTIVE') && a.provider_id === p.id).length;
                      const isPSelected = selectedProviderFilter === p.id;
                      return (
                        <button
                          key={p.id}
                          onClick={() => setSelectedProviderFilter(p.id)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer flex items-center space-x-1.5 ${
                            isPSelected
                              ? 'bg-gradient-to-b from-sky-600 via-sky-700 to-blue-800 text-white shadow-[0_1px_6px_rgba(2,132,199,0.3),inset_0_1px_0_rgba(255,255,255,0.2)] border border-sky-400/40'
                              : 'bg-slate-900/90 text-slate-300 hover:text-white hover:bg-slate-800 border border-slate-800/90 shadow-xs'
                          }`}
                        >
                          <span>{p.name}</span>
                          <span className={`text-[9.5px] font-mono px-1 py-0.2 rounded font-bold leading-none ${
                            isPSelected ? 'bg-white/20 text-white' : 'bg-slate-950 text-white'
                          }`}>
                            {pActiveCount}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* SUBTAB A: CLIENT -> TREATING PROVIDER ASSIGNMENTS */}
              {relationshipSubTab === 'CLIENT_ASSIGNMENTS' && (
                <div className="rounded-xl overflow-hidden border border-slate-700/70 mobile-ops-shell sm:bg-gradient-to-b sm:from-[#18263c] sm:via-[#131d2e] sm:to-[#0f1826] shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                  <div className="px-3.5 py-2 bg-white border-b border-slate-200/90 shadow-2xs flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-slate-900 text-[11.5px] tracking-tight">Active Caseload Assignments</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                    </div>
                    <span className="font-mono text-[10.5px] text-slate-800 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 font-medium">
                      {filteredAssignments.length} records
                    </span>
                  </div>

                  {filteredAssignments.length === 0 ? (
                    <div className="p-8 text-center text-slate-600 text-xs">
                      No active client assignments found. Click 'Assign Client' to create one.
                    </div>
                  ) : (
                    <>
                      {/* MOBILE VIEW: Wrapped Assignment Cards (Phone Widths Only, sm:hidden) */}
                      <div className="block sm:hidden divide-y divide-slate-800/60">
                        {filteredAssignments.map((a, idx) => (
                          <div
                            key={`mobile-${a.id}`}
                            className={`p-3.5 ${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} hover:from-[#223554] hover:to-[#1a2b42] transition-colors flex flex-col gap-2.5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]`}
                          >
                            {/* Line 1: Strongest element - Client Name + Live Tag + View Chart button */}
                            <div className="flex items-start justify-between gap-2">
                              <button
                                onClick={() => onSelectClient(a.client_id)}
                                className="font-bold text-slate-100 hover:text-cyan-300 text-[13px] leading-snug transition-colors text-left flex items-center gap-1.5 flex-wrap cursor-pointer group"
                              >
                                <span className="text-white group-hover:text-cyan-200">
                                  {a.client_last_name}, {a.client_first_name}
                                </span>
                                {liveSessions.some(s => s.clientId === a.client_id) && (
                                  <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded bg-emerald-950/90 border border-emerald-500/40 text-[9px] font-semibold text-emerald-300 shrink-0">
                                    <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                                    <span>IN SESSION</span>
                                  </span>
                                )}
                              </button>

                              <button
                                onClick={() => onSelectClient(a.client_id)}
                                className="px-2.5 py-1 rounded-md bg-sky-50 hover:bg-sky-100 text-sky-800 hover:text-sky-950 border border-sky-200 text-[11px] font-bold shadow-xs transition-all cursor-pointer inline-flex items-center gap-1 shrink-0 active:scale-95"
                              >
                                <span>View Chart</span>
                                <ChevronRight className="w-3 h-3 text-sky-400" />
                              </button>
                            </div>

                            {/* Client ID / AHCCCS badge line */}
                            <div className="text-[11px] font-mono text-slate-400 leading-tight">
                              AHCCCS: <span className="text-sky-400 font-semibold">{a.ahcccs_id || a.client_id}</span>
                            </div>

                            {/* Line 2: Treating Provider & Supervisor Information */}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1 border-t border-slate-800/40 text-xs">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-slate-400 text-[11px]">Provider:</span>
                                <span className="font-semibold text-slate-200 text-xs">{a.provider_name}</span>
                                <span className="text-[9.5px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-800 border border-slate-200 leading-none">
                                  {a.provider_role}
                                </span>
                              </div>

                              {a.supervisor_name ? (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-slate-500 text-[11px]">• Sup:</span>
                                  <span className="font-medium text-indigo-300 text-xs">{a.supervisor_name}</span>
                                </div>
                              ) : (
                                <div className="text-slate-500 text-[10.5px]">
                                  • Direct Practitioner
                                </div>
                              )}
                            </div>

                            {/* Line 3: Operational Cadence, Assigned Date & Manual Exception */}
                            <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-slate-800/40">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-800 leading-none">
                                  {a.expected_frequency || '2x/week'}
                                </span>
                                <span className="text-[10px] font-mono font-medium px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-800 leading-none">
                                  {a.expected_duration_minutes || 60}m
                                </span>
                                {a.assigned_date && (
                                  <span className="text-[10px] font-mono text-slate-400">
                                    Assigned {a.assigned_date}
                                  </span>
                                )}
                              </div>

                              {/* Manual SOAP Exception status and optional Edit action */}
                              <div className="flex items-center gap-1.5">
                                {a.manual_soap_approved ? (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-bold font-mono bg-amber-950/70 text-amber-300 border border-amber-500/35 shadow-[0_0_6px_rgba(245,158,11,0.15)] leading-tight">
                                    SOAP: {a.manual_soap_reason || 'Approved'}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] text-slate-700 bg-slate-100 border border-slate-200 leading-tight">
                                    SOAP: Standard
                                  </span>
                                )}

                                {canEditRemoteSignature && (
                                  <button
                                    onClick={() => setManualSoapAssignment(a)}
                                    className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-800 hover:bg-slate-700 text-cyan-300 border border-slate-700/80 transition-colors cursor-pointer"
                                    title="Configure Manual SOAP exception lane"
                                  >
                                    Edit
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* DESKTOP/TABLET VIEW: Full Standard Table (sm:block, hidden on mobile) */}
                      <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                          <thead className="bg-gradient-to-b from-[#111928] to-[#0b101b] text-[9.5px] uppercase text-slate-300 font-bold tracking-wider border-b border-slate-700/70 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
                            <tr>
                              <th className="px-3 py-1.5 font-bold tracking-wider text-slate-300">Client</th>
                              <th className="px-3 py-1.5 font-bold tracking-wider text-slate-300">Treating Provider</th>
                              <th className="px-3 py-1.5 font-bold tracking-wider text-slate-300">Supervising SLP</th>
                              <th className="px-3 py-1.5 font-bold tracking-wider text-slate-300">Expected Frequency</th>
                              <th className="px-3 py-1.5 font-bold tracking-wider text-slate-300">Expected Duration</th>
                              <th className="px-3 py-1.5 font-bold tracking-wider text-slate-300">Assigned Date</th>
                              <th className="px-3 py-1.5 font-bold tracking-wider text-slate-300">Manual SOAP Exception</th>
                              <th className="px-3 py-1.5 font-bold tracking-wider text-slate-300 text-right">View Chart</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800/50">
                            {filteredAssignments.map((a) => (
                              <tr
                                key={a.id}
                                className="border-l-2 border-l-transparent hover:border-l-cyan-400/80 hover:bg-[#20314a] transition-all duration-150 group odd:bg-[#18263a] even:bg-[#101827] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]"
                              >
                                {/* Client */}
                                <td className="px-3 py-1.5">
                                  <button
                                    onClick={() => onSelectClient(a.client_id)}
                                    className="font-bold text-slate-100 hover:text-cyan-300 text-xs transition-colors text-left flex items-center space-x-1.5 group-hover:text-cyan-200 cursor-pointer flex-wrap"
                                  >
                                    <span>{a.client_last_name}, {a.client_first_name}</span>
                                    {liveSessions.some(s => s.clientId === a.client_id) && (
                                      <span className="inline-flex items-center space-x-1 px-1.5 py-0.2 rounded bg-emerald-950/80 border border-emerald-500/30 text-[9px] font-semibold text-emerald-300">
                                        <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                                        <span>IN SESSION</span>
                                      </span>
                                    )}
                                    <ChevronRight className="w-3 h-3 text-slate-500 group-hover:text-cyan-400 transition-colors" />
                                  </button>
                                  <span className="font-mono text-[9.5px] text-slate-400 block leading-tight mt-0.5">
                                    AHCCCS: <span className="text-cyan-400 font-semibold">{a.ahcccs_id || a.client_id}</span>
                                  </span>
                                </td>

                                {/* Treating Provider */}
                                <td className="px-3 py-1.5">
                                  <div className="font-bold text-slate-100 text-xs leading-tight">{a.provider_name}</div>
                                  <span className="text-[9.5px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-800 border border-slate-200 inline-block mt-0.5 leading-none">
                                    {a.provider_role}
                                  </span>
                                </td>

                                {/* Supervising SLP */}
                                <td className="px-3 py-1.5">
                                  {a.supervisor_name ? (
                                    <div>
                                      <div className="font-semibold text-slate-200 text-xs leading-tight">{a.supervisor_name}</div>
                                      <span className="text-[9.5px] text-indigo-400 block leading-tight mt-0.5">Supervisor</span>
                                    </div>
                                  ) : (
                                    <span className="text-slate-500 text-[10.5px] font-medium">Direct Practitioner</span>
                                  )}
                                </td>

                                {/* Expected Frequency */}
                                <td className="px-3 py-1.5 font-mono">
                                  <span className="text-[11px] text-slate-800 font-semibold px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 leading-none inline-block">
                                    {a.expected_frequency || '2x/week'}
                                  </span>
                                </td>

                                {/* Expected Duration */}
                                <td className="px-3 py-1.5 font-mono">
                                  <span className="text-[11px] text-slate-800 font-semibold px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 leading-none inline-block">
                                    {a.expected_duration_minutes || 60}m
                                  </span>
                                </td>

                                {/* Assigned Date */}
                                <td className="px-3 py-1.5 font-mono text-slate-400 text-[10.5px]">
                                  {a.assigned_date || '—'}
                                </td>

                                {/* Manual SOAP Exception */}
                                <td className="px-3 py-1.5">
                                  <div className="flex items-center space-x-1.5">
                                    {a.manual_soap_approved ? (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-bold font-mono bg-amber-950/70 text-amber-300 border border-amber-500/35 shadow-[0_0_6px_rgba(245,158,11,0.15)] leading-tight">
                                        Approved: {a.manual_soap_reason || 'Exception'}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] text-slate-700 bg-slate-100 border border-slate-200 leading-tight">
                                        Disabled
                                      </span>
                                    )}
                                    {canEditRemoteSignature && (
                                      <button
                                        onClick={() => setManualSoapAssignment(a)}
                                        className="px-1.5 py-0.5 rounded text-[9.5px] font-semibold bg-slate-800 hover:bg-slate-700 text-cyan-300 border border-slate-700/80 transition-colors cursor-pointer"
                                        title="Configure Manual SOAP exception lane"
                                      >
                                        Edit
                                      </button>
                                    )}
                                  </div>
                                </td>

                                {/* View Chart */}
                                <td className="px-3 py-1.5 text-right">
                                  <button
                                    onClick={() => onSelectClient(a.client_id)}
                                    className="px-2.5 py-1 rounded-md bg-sky-50 hover:bg-sky-100 text-sky-800 hover:text-sky-950 border border-sky-200 hover:border-sky-300 text-[11px] font-bold shadow-xs transition-all cursor-pointer inline-flex items-center gap-1 active:scale-98"
                                  >
                                    <span>View Chart</span>
                                    <ChevronRight className="w-3 h-3 text-cyan-400/70 group-hover:translate-x-0.5 transition-transform" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

          {/* SUBTAB B: SLPA -> SUPERVISOR OVERSIGHT */}
          {relationshipSubTab === 'SUPERVISOR_OVERSIGHT' && (
            <div className="space-y-3">
              {supervisors.map((sup) => {
                const supervisedAssistants = assistants.filter(ast => (ast as any).supervisor_id === sup.id);
                return (
                  <div
                    key={sup.id}
                    className="rounded-xl border border-slate-200/90 bg-white overflow-hidden shadow-sm hover:border-slate-300 transition-all"
                  >
                    {/* Compact Supervisor Header Row */}
                    <div className="px-3.5 py-2 bg-slate-100 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center space-x-2.5 min-w-0">
                        <div className="w-6 h-6 rounded bg-slate-800 border border-slate-700 flex items-center justify-center text-white font-bold text-[10px] shrink-0 shadow-xs">
                          <Shield className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="flex items-center space-x-2 flex-wrap min-w-0">
                          <span className="font-bold text-slate-900 text-xs tracking-tight">{sup.name}</span>
                          <span className="text-[9.5px] font-mono px-1.5 py-0.2 rounded bg-white text-slate-700 border border-slate-200 leading-none">
                            {sup.role}
                          </span>
                          <span className="text-[10.5px] text-slate-600 truncate">
                            {sup.title || 'Supervising Speech-Language Pathologist'}
                          </span>
                        </div>
                      </div>

                      <span className="text-[10.5px] font-mono font-semibold text-slate-700">
                        {supervisedAssistants.length} Supervised Assistant{supervisedAssistants.length === 1 ? '' : 's'}
                      </span>
                    </div>

                    {/* Supervised Assistants List */}
                    {supervisedAssistants.length === 0 ? (
                      <div className="px-4 py-3 text-center text-slate-500 text-xs italic bg-slate-50">
                        No assistants currently supervised by this clinician.
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-200 bg-white">
                        {supervisedAssistants.map((ast) => {
                          const astAssignments = assignments.filter(a => a.provider_id === ast.id);
                          return (
                            <div
                              key={ast.id}
                              className="px-3.5 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white hover:bg-slate-50 transition-colors"
                            >
                              <div className="flex items-center space-x-2.5 min-w-0">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0" />
                                <div className="min-w-0">
                                  <div className="flex items-center space-x-2 flex-wrap">
                                    <span className="font-semibold text-slate-800 text-xs">{ast.name}</span>
                                    <span className="text-[9.5px] font-mono font-semibold px-1.5 py-0.2 rounded bg-slate-100 border border-slate-200 text-slate-700 leading-none">
                                      {ast.role || 'SLPA'}
                                    </span>
                                    <span className="text-[10.5px] font-mono text-slate-500">
                                      <span className="text-slate-800 font-semibold">{astAssignments.length}</span> Assigned Client{astAssignments.length === 1 ? '' : 's'}
                                    </span>
                                    {ast.rate_per_session !== null && ast.rate_per_session !== undefined && (
                                      <span className="text-[10px] font-mono text-slate-500">
                                        • Rate: ${ast.rate_per_session}/sess
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <span className="text-[9.5px] font-mono font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 shrink-0 self-end sm:self-center">
                                Compliant Oversight
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* SUBTAB C: BY PROVIDER */}
          {relationshipSubTab === 'BY_PROVIDER' && (
            <div className="space-y-3">
              {providers
                .filter(p => selectedProviderFilter === 'ALL' || p.id === selectedProviderFilter)
                .map((p) => {
                  const provClients = filteredAssignments.filter(a => a.provider_id === p.id);
                  return (
                    <div
                      key={p.id}
                      className="rounded-xl border border-slate-700/70 bg-gradient-to-b from-[#18263c] via-[#131d2e] to-[#0f1826] overflow-hidden shadow-sm hover:border-slate-600/80 transition-all"
                    >
                      {/* Compact Provider Header Row: Provider | role / supervision context | active clients */}
                      <div className="px-3.5 py-2 bg-gradient-to-r from-[#20314c] via-[#18263d] to-[#142033] border-b border-slate-700/70 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center space-x-2.5 min-w-0">
                          <div className="w-6 h-6 rounded bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-300 font-bold text-[10px] shrink-0 shadow-xs">
                            {p.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="flex items-center space-x-2 flex-wrap min-w-0">
                            <span className="font-bold text-white text-xs tracking-tight truncate">{p.name}</span>
                            <span className="text-[9.5px] font-mono px-1.5 py-0.2 rounded bg-transparent text-slate-200 border border-slate-400/35 leading-none">
                              {p.role}
                            </span>
                            <span className="text-[10.5px] text-slate-400 truncate">
                              {p.title || (p.role === 'SLPA' ? 'Speech-Language Pathology Assistant' : 'Speech-Language Pathologist')}
                              {p.supervisor_name && (
                                <span className="ml-1.5 text-indigo-300">
                                  • Supervising SLP: <span className="font-semibold text-indigo-200">{p.supervisor_name}</span>
                                </span>
                              )}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 shrink-0">
                          <span className="text-[10.5px] font-mono font-semibold text-sky-200">
                            {provClients.length} Active {provClients.length === 1 ? 'Client' : 'Clients'}
                          </span>
                          {isOfficeOrAdmin && (
                            <button
                              onClick={() => {
                                setAssignForm(prev => ({ ...prev, provider_id: p.id }));
                                setShowAssignModal(true);
                              }}
                              className="px-2 py-0.5 text-[10.5px] font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white border border-slate-700 rounded transition-colors flex items-center space-x-1 cursor-pointer"
                            >
                              <Plus className="w-3 h-3 text-slate-400" />
                              <span>Assign</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Assigned Active Clients Underneath as Flat Roster Rows */}
                      {provClients.length === 0 ? (
                        <div className="py-3 px-4 text-center text-xs text-slate-500 italic bg-slate-950/30">
                          No active clients currently assigned to {p.name}.
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-800/60 bg-transparent">
                          {provClients.map((a, idx) => {
                            const todayIso = new Date().toISOString().slice(0, 10);
                            const weekStats = getWeeklySessionStats(a.client_id, todayIso, services, assignments);
                            const freq = a.auth_frequency || a.expected_frequency || '2x/week';
                            const duration = a.expected_duration_minutes || 60;

                            let progressBadgeClass = 'bg-slate-100 text-slate-700 border-slate-200';
                            let progressDotClass = 'bg-slate-500';
                            let progressText = `${weekStats.totalCount} visit(s) planned`;

                            if (weekStats.freqInfo.isWeekly) {
                              if (weekStats.exceeds) {
                                progressBadgeClass = 'bg-rose-50 text-rose-800 border-rose-200';
                                progressDotClass = 'bg-rose-600';
                                progressText = `${weekStats.totalCount} of ${weekStats.freqInfo.count} planned (over)`;
                              } else if (weekStats.totalCount === weekStats.freqInfo.count && weekStats.totalCount > 0) {
                                progressBadgeClass = 'bg-emerald-50 text-emerald-800 border-emerald-200';
                                progressDotClass = 'bg-emerald-600';
                                progressText = `${weekStats.totalCount} of ${weekStats.freqInfo.count} planned`;
                              } else if (weekStats.totalCount > 0) {
                                progressBadgeClass = 'bg-amber-50 text-amber-800 border-amber-200';
                                progressDotClass = 'bg-amber-600';
                                progressText = `${weekStats.totalCount} of ${weekStats.freqInfo.count} planned`;
                              } else {
                                progressBadgeClass = 'bg-slate-100 text-slate-700 border-slate-200';
                                progressDotClass = 'bg-slate-500';
                                progressText = `0 of ${weekStats.freqInfo.count} planned`;
                              }
                            }

                            return (
                              <div
                                key={a.id}
                                tabIndex={0}
                                role="button"
                                onClick={() => onSelectClient(a.client_id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onSelectClient(a.client_id);
                                  }
                                }}
                                className={`group relative py-2 px-3 sm:px-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-2 ${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} md:odd:bg-[#1c2c46] md:even:bg-[#223554] md:hover:bg-[#283e62] cursor-pointer select-none transition-colors duration-150 focus:outline-none focus:bg-[#283e62]`}
                              >
                                {/* Client Name + AHCCCS */}
                                <div className="flex items-center space-x-2.5 min-w-0">
                                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0 shadow-[0_0_6px_rgba(6,182,212,0.6)]" />
                                  <div className="min-w-0">
                                    <div className="flex items-center space-x-2 flex-wrap">
                                      <span className="font-semibold text-slate-100 group-hover:text-cyan-200 text-xs transition-colors text-left truncate flex items-center space-x-1">
                                        <span>{a.client_last_name}, {a.client_first_name}</span>
                                        <ChevronRight className="w-3 h-3 text-slate-500 group-hover:text-cyan-400 transition-colors" />
                                      </span>
                                      <span className="text-[9.5px] font-mono text-slate-500">
                                        AHCCCS: <span className="text-slate-300 font-semibold">{a.ahcccs_id || a.client_id}</span>
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Operational Metrics & Actions */}
                                <div className="flex items-center space-x-2 sm:space-x-3 shrink-0 self-end sm:self-center">
                                  {/* Frequency & Duration */}
                                  <div className="flex items-center space-x-1 font-mono">
                                    <span className="text-[9.5px] font-semibold px-1.5 py-0.2 rounded bg-slate-100 border border-slate-200 text-slate-800 leading-none">
                                      {freq}
                                    </span>
                                    <span className="text-[9.5px] text-slate-800">
                                      {duration}m
                                    </span>
                                  </div>

                                  {/* This-Week Progress */}
                                  <span className={`text-[9.5px] font-mono font-semibold px-2 py-0.5 rounded border inline-flex items-center gap-1.5 leading-none ${progressBadgeClass}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${progressDotClass}`} />
                                    <span>{progressText}</span>
                                  </span>

                                  {/* Actions */}
                                  <div className="flex items-center space-x-1.5">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onSelectClient(a.client_id);
                                      }}
                                      className="px-2 py-0.5 text-[11px] font-semibold rounded bg-gradient-to-b from-slate-900/90 to-slate-950/90 hover:from-slate-800 hover:to-slate-900 text-sky-200 hover:text-white border border-sky-500/35 hover:border-sky-400/60 shadow-xs transition-all cursor-pointer flex items-center space-x-0.5 active:scale-98"
                                    >
                                      <span>Chart</span>
                                      <ChevronRight className="w-3 h-3 text-sky-400/80" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openPlanVisitModal(a.client_id, a.provider_id);
                                      }}
                                      className={`px-2 py-0.5 text-[11px] font-semibold rounded items-center space-x-1 active:scale-98 transition-all cursor-pointer ${
                                        isAssistant
                                          ? 'flex bg-gradient-to-r from-purple-950/90 to-indigo-950/90 hover:from-purple-900 hover:to-indigo-900 text-purple-200 hover:text-white border border-purple-500/45 hover:border-purple-400/70 shadow-[0_1px_4px_rgba(147,51,234,0.25),inset_0_1px_0_rgba(255,255,255,0.12)] sm:bg-none sm:bg-slate-800 sm:hover:bg-slate-700 sm:text-white sm:border-slate-700 sm:shadow-xs sm:hover:shadow-none'
                                          : 'hidden sm:flex bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 shadow-xs'
                                      }`}
                                    >
                                      <Calendar className={`w-3 h-3 ${isAssistant ? 'text-purple-300 sm:text-slate-300' : 'text-slate-300'}`} />
                                      <span>Plan Visit</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* TAB 2: COMPLETED SERVICES / OPERATIONAL VISIT LOG */}
      {activeTab === 'COMPLETED_SERVICES' && (
        <div className="space-y-3">
          {/* TOP CONTROLS CARD: PROVIDER SELECTOR, PAY PERIOD NAVIGATION, SEARCH */}
          <div className="relative overflow-visible bg-gradient-to-b from-[#162236] to-[#0f1726] p-3.5 sm:p-4 rounded-xl border border-slate-700/80 shadow-[0_4px_16px_rgba(0,0,0,0.5),inset_0_1px_0_0_rgba(255,255,255,0.06)] space-y-3">
            {/* Top specular hairline highlight */}
            <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-sky-400/25 to-transparent pointer-events-none" />

            {/* Row 1: Primary Title + Action button */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              <div className="flex items-center space-x-2.5">
                <div className="w-7 h-7 rounded-lg bg-sky-500/15 border border-sky-400/30 flex items-center justify-center shrink-0 shadow-[0_0_8px_rgba(56,189,248,0.15)]">
                  <CheckCircle className="w-4 h-4 text-sky-400" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-bold text-white tracking-wide">
                      {isOperationalRole ? 'Completed Therapy Services & Visit History' : 'Visit Log'}
                    </span>
                    <span className="px-2 py-0.5 rounded font-mono text-[10px] font-bold bg-sky-950/80 text-sky-300 border border-sky-700/60 shadow-xs">
                      {filteredCompletedServices.length} {filteredCompletedServices.length === 1 ? 'visit' : 'visits'}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Operational service activity by provider and pay period. Reflects canonical rendered visits, signatures, and billing status.
                  </p>
                </div>
              </div>

              {canManualServiceEntry && (
                <button
                  type="button"
                  onClick={() => setShowRecordServiceModal(true)}
                  className="relative group overflow-hidden bg-slate-800/90 hover:bg-slate-700/90 text-slate-200 hover:text-white border border-slate-700/80 hover:border-slate-500/70 font-semibold px-3 py-1.5 rounded-lg text-xs flex items-center space-x-1.5 transition-all duration-200 shrink-0 shadow-sm cursor-pointer self-start sm:self-center"
                  title="Record an exceptional service or historical backfill outside canonical SOAP notes"
                >
                  <span className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-600 ease-out bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />
                  <Plus className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-200 transition-colors" />
                  <span>Manual Service Entry (Exception / Backfill)</span>
                </button>
              )}
            </div>

            {/* Row 2: Operational Filter Bar (Provider + Pay Period + Search) */}
            <div className="pt-2 border-t border-slate-700/60 grid grid-cols-1 md:grid-cols-12 gap-2.5 items-end">
              {/* 1. PROVIDER SELECTOR */}
              <div className="md:col-span-4 relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    <Stethoscope className="w-3 h-3 text-sky-400" />
                    <span>Provider</span>
                  </label>
                  {selectedVisitLogProviderId !== 'ALL' && (
                    <button
                      type="button"
                      onClick={() => setSelectedVisitLogProviderId('ALL')}
                      className="text-[10px] text-sky-400 hover:text-sky-300 font-semibold cursor-pointer"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <div className="relative">
                  <button
                    id="provider-filter-button"
                    type="button"
                    onClick={() => {
                      setIsProviderDropdownOpen(prev => !prev);
                      setIsPeriodDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all cursor-pointer shadow-xs focus:outline-none focus:ring-1 focus:ring-slate-300 ${
                      selectedVisitLogProviderId !== 'ALL'
                        ? 'bg-sky-50 border-sky-300 text-sky-950 hover:bg-sky-100/70'
                        : 'bg-white hover:bg-slate-50 border-slate-300 hover:border-slate-400 text-slate-900'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${selectedVisitLogProviderId !== 'ALL' ? 'bg-sky-600' : 'bg-slate-400'}`} />
                      <span className="truncate">
                        {selectedVisitLogProvider ? (
                          <>
                            <span className="font-semibold text-slate-900">{selectedVisitLogProvider.name}</span>
                            {selectedVisitLogProvider.role && (
                              <span className="ml-1 text-[10px] text-sky-700 bg-sky-100 border border-sky-200/80 px-1 py-0.2 rounded font-mono font-bold">
                                ({selectedVisitLogProvider.role})
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="font-semibold text-slate-900">All Providers</span>
                        )}
                      </span>
                    </div>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-600 shrink-0 transition-transform ${isProviderDropdownOpen ? 'rotate-180 text-slate-900' : ''}`} />
                  </button>

                  {/* Popover */}
                  {isProviderDropdownOpen && (
                    <div
                      id="provider-filter-popover"
                      className="absolute z-50 left-0 right-0 sm:w-80 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 text-slate-900"
                    >
                      <div className="p-2 border-b border-slate-200 bg-slate-50">
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            value={providerSearchTerm}
                            onChange={(e) => setProviderSearchTerm(e.target.value)}
                            placeholder="Search provider, role, ID..."
                            className="w-full pl-8 pr-2.5 py-1 text-xs bg-white border border-slate-300 rounded-md text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-300"
                            autoFocus
                          />
                        </div>
                      </div>

                      <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 p-1">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedVisitLogProviderId('ALL');
                            setIsProviderDropdownOpen(false);
                            setProviderSearchTerm('');
                          }}
                          className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs flex items-center justify-between transition-colors cursor-pointer ${
                            selectedVisitLogProviderId === 'ALL'
                              ? 'bg-slate-100 text-slate-900 font-bold'
                              : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-slate-400" />
                            <span className="font-semibold">All Providers</span>
                          </div>
                          {selectedVisitLogProviderId === 'ALL' && <Check className="w-3.5 h-3.5 text-slate-900" />}
                        </button>

                        {searchableVisitLogProviders.map(p => {
                          const isSelected = selectedVisitLogProviderId === p.id;
                          return (
                            <button
                              key={`prov-opt-${p.id}`}
                              type="button"
                              onClick={() => {
                                setSelectedVisitLogProviderId(p.id);
                                setIsProviderDropdownOpen(false);
                                setProviderSearchTerm('');
                              }}
                              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs flex items-center justify-between transition-colors cursor-pointer ${
                                isSelected
                                  ? 'bg-slate-100 text-slate-900 font-bold'
                                  : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                              }`}
                            >
                              <div className="min-w-0 pr-2">
                                <div className="truncate text-slate-900 font-medium">{p.name}</div>
                                <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                                  <span className="px-1 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 font-mono font-bold">
                                    {p.role || 'PROVIDER'}
                                  </span>
                                  <span className="font-mono text-slate-400">{p.id}</span>
                                </div>
                              </div>
                              {isSelected && <Check className="w-3.5 h-3.5 text-slate-900 shrink-0" />}
                            </button>
                          );
                        })}

                        {searchableVisitLogProviders.length === 0 && (
                          <div className="p-3 text-center text-xs text-slate-500">
                            No providers match &ldquo;{providerSearchTerm}&rdquo;
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 2. CANONICAL PAY PERIOD NAVIGATION */}
              <div className="md:col-span-5 relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3 text-sky-400" />
                    <span>Pay Period</span>
                  </label>
                  {selectedPayPeriod !== 'ALL' && !selectedPayPeriod.isCurrent && (
                    <button
                      type="button"
                      onClick={() => setSelectedPayPeriod(getCurrentPayPeriod())}
                      className="text-[10px] text-emerald-400 hover:text-emerald-300 font-semibold cursor-pointer"
                    >
                      Jump to Current
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {/* Previous Period Button */}
                  <button
                    type="button"
                    onClick={handlePrevPeriod}
                    title="Previous Pay Period"
                    className="p-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-300 hover:border-slate-400 transition-colors cursor-pointer shrink-0 shadow-xs"
                  >
                    <ChevronLeft className="w-4 h-4 text-slate-600" />
                  </button>

                  {/* Period Dropdown Button */}
                  <button
                    id="period-filter-button"
                    type="button"
                    onClick={() => {
                      setIsPeriodDropdownOpen(prev => !prev);
                      setIsProviderDropdownOpen(false);
                    }}
                    className={`flex-1 flex items-center justify-between gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-mono font-medium transition-all cursor-pointer shadow-xs focus:outline-none focus:ring-1 focus:ring-slate-300 ${
                      selectedPayPeriod !== 'ALL' && selectedPayPeriod.isCurrent
                        ? 'bg-emerald-50/60 border-emerald-300/80 text-emerald-950 hover:bg-emerald-100/50'
                        : selectedPayPeriod === 'ALL'
                        ? 'bg-white hover:bg-slate-50 border-slate-300 hover:border-slate-400 text-slate-900'
                        : 'bg-sky-50/60 border-sky-300/80 text-sky-950 hover:bg-sky-100/50'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 truncate">
                      <span className="truncate font-semibold text-slate-900">
                        {selectedPayPeriod === 'ALL' ? 'All Pay Periods' : selectedPayPeriod.label}
                      </span>
                      {selectedPayPeriod !== 'ALL' && selectedPayPeriod.isCurrent && (
                        <span className="px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 text-[9px] font-bold font-sans">
                          CURRENT
                        </span>
                      )}
                    </div>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-600 shrink-0 transition-transform ${isPeriodDropdownOpen ? 'rotate-180 text-slate-900' : ''}`} />
                  </button>

                  {/* Next Period Button */}
                  <button
                    type="button"
                    onClick={handleNextPeriod}
                    title="Next Pay Period"
                    className="p-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-300 hover:border-slate-400 transition-colors cursor-pointer shrink-0 shadow-xs"
                  >
                    <ChevronRight className="w-4 h-4 text-slate-600" />
                  </button>

                  {/* Period Popover */}
                  {isPeriodDropdownOpen && (
                    <div
                      id="period-filter-popover"
                      className="absolute z-50 left-0 right-0 sm:w-84 mt-1 top-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 text-slate-900"
                    >
                      <div className="p-2 border-b border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-600">
                        Select Allegiant Pay Period (Biweekly)
                      </div>
                      <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 p-1">
                        {/* Current Period Quick Select */}
                        {(() => {
                          const curr = getCurrentPayPeriod();
                          const isSel = selectedPayPeriod !== 'ALL' && selectedPayPeriod.id === curr.id;
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedPayPeriod(curr);
                                setIsPeriodDropdownOpen(false);
                              }}
                              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs flex items-center justify-between transition-colors cursor-pointer ${
                                isSel ? 'bg-emerald-50 text-emerald-950 font-bold border border-emerald-200/60' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                              }`}
                            >
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-slate-900">{curr.label}</span>
                                <span className="px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 text-[9px] font-bold">
                                  CURRENT
                                </span>
                              </div>
                              {isSel && <Check className="w-3.5 h-3.5 text-emerald-700" />}
                            </button>
                          );
                        })()}

                        <div className="py-1 px-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
                          Recent Pay Periods
                        </div>

                        {recentPayPeriods.map(p => {
                          const isSel = selectedPayPeriod !== 'ALL' && selectedPayPeriod.id === p.id;
                          return (
                            <button
                              key={`period-opt-${p.id}`}
                              type="button"
                              onClick={() => {
                                setSelectedPayPeriod(p);
                                setIsPeriodDropdownOpen(false);
                              }}
                              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs flex items-center justify-between transition-colors cursor-pointer ${
                                isSel ? 'bg-slate-100 text-slate-900 font-bold' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                              }`}
                            >
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-slate-900">{p.label}</span>
                                {p.isCurrent && (
                                  <span className="px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 text-[9px] font-bold">
                                    CURRENT
                                  </span>
                                )}
                              </div>
                              {isSel && <Check className="w-3.5 h-3.5 text-slate-900" />}
                            </button>
                          );
                        })}

                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPayPeriod('ALL');
                              setIsPeriodDropdownOpen(false);
                            }}
                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs flex items-center justify-between transition-colors cursor-pointer ${
                              selectedPayPeriod === 'ALL'
                                ? 'bg-slate-100 text-slate-900 font-bold'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                            }`}
                          >
                            <span>All Pay Periods (All-Time Unfiltered)</span>
                            {selectedPayPeriod === 'ALL' && <Check className="w-3.5 h-3.5 text-slate-900" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 3. SEARCH INPUT */}
              <div className="md:col-span-3">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-1">
                  <Search className="w-3 h-3 text-slate-400" />
                  <span>Filter Visits</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={visitLogSearchQuery}
                    onChange={(e) => setVisitLogSearchQuery(e.target.value)}
                    placeholder="Client, CPT, note..."
                    className="w-full px-3 py-1.5 text-xs bg-slate-900/90 border border-slate-700/80 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-sky-500"
                  />
                  {visitLogSearchQuery && (
                    <button
                      type="button"
                      onClick={() => setVisitLogSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Active Filters Context Strip */}
            <div className="pt-2 border-t border-slate-700/40 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-400">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span>Displaying:</span>
                <span className="font-semibold text-white">
                  {selectedVisitLogProvider ? selectedVisitLogProvider.name : 'All Providers'}
                </span>
                <span className="text-slate-600">•</span>
                <span className="font-mono text-sky-300 font-medium">
                  {selectedPayPeriod === 'ALL' ? 'All-Time' : selectedPayPeriod.label}
                </span>
                {selectedPayPeriod !== 'ALL' && selectedPayPeriod.isCurrent && (
                  <span className="px-1 py-0.2 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[9.5px] font-bold">
                    ACTIVE CYCLE
                  </span>
                )}
                {visitLogSearchQuery && (
                  <>
                    <span className="text-slate-600">•</span>
                    <span className="text-amber-300">matching &ldquo;{visitLogSearchQuery}&rdquo;</span>
                  </>
                )}
              </div>

              {(selectedVisitLogProviderId !== 'ALL' || selectedPayPeriod !== 'ALL' || visitLogSearchQuery) && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedVisitLogProviderId('ALL');
                    setSelectedPayPeriod(getCurrentPayPeriod());
                    setVisitLogSearchQuery('');
                  }}
                  className="text-slate-400 hover:text-white underline text-[11px] cursor-pointer"
                >
                  Reset filters
                </button>
              )}
            </div>
          </div>

          {/* TABLE CONTAINER: OPERATIONAL SESSIONS */}
          <div className="relative rounded-xl border border-slate-700/80 mobile-ops-shell md:bg-gradient-to-b md:from-[#0e1626] md:via-[#0a101d] md:to-[#070b14] shadow-[0_8px_24px_rgba(0,0,0,0.55),0_1px_3px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.06)] overflow-hidden">
            {/* Top specular hairline */}
            <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/25 to-transparent pointer-events-none" />

            {filteredCompletedServices.length === 0 ? (
              <div className="p-10 text-center text-slate-400 space-y-3">
                <FileText className="w-8 h-8 text-slate-600 mx-auto stroke-[1.5]" />
                <div className="text-sm font-semibold text-slate-200">
                  No completed sessions found
                </div>
                <p className="text-xs text-slate-500 max-w-md mx-auto">
                  {selectedVisitLogProviderId !== 'ALL'
                    ? `No completed visits for ${selectedVisitLogProvider?.name || 'this provider'} in the selected pay period.`
                    : 'No completed visits match the selected provider and pay period filters.'}
                </p>
                <div className="flex items-center justify-center gap-2 pt-1">
                  {selectedVisitLogProviderId !== 'ALL' && (
                    <button
                      type="button"
                      onClick={() => setSelectedVisitLogProviderId('ALL')}
                      className="px-3 py-1 text-xs rounded-lg bg-slate-800 hover:bg-slate-700 text-sky-300 border border-slate-700 cursor-pointer"
                    >
                      Show All Providers
                    </button>
                  )}
                  {selectedPayPeriod !== 'ALL' && (
                    <button
                      type="button"
                      onClick={() => setSelectedPayPeriod('ALL')}
                      className="px-3 py-1 text-xs rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 cursor-pointer"
                    >
                      Show All Periods
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Desktop/Tablet Table (md and up) */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-slate-700/70 text-[9.5px] font-bold text-slate-300 uppercase tracking-wider bg-gradient-to-b from-[#111928] to-[#0b101b] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
                        <th className="px-3 py-2.5 font-semibold">Client</th>
                        <th className="px-3 py-2.5 font-semibold">DOS & Time</th>
                        <th className="px-3 py-2.5 font-semibold">Treating Provider</th>
                        <th className="hidden lg:table-cell px-3 py-2.5 font-semibold">CPT</th>
                        <th className="hidden lg:table-cell px-3 py-2.5 font-semibold">Duration</th>
                        <th className="hidden xl:table-cell px-3 py-2.5 font-semibold">Linked Clinical Note</th>
                        <th className="hidden 2xl:table-cell px-2.5 py-2.5 font-semibold">Guardian Sig</th>
                        <th className="hidden 2xl:table-cell px-2.5 py-2.5 font-semibold">Supervisor Sig</th>
                        <th className="hidden 2xl:table-cell px-2.5 py-2.5 font-semibold">Clinician Sig</th>
                        <th className="px-2.5 py-2.5 font-semibold">Billing Readiness</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/70">
                      {filteredCompletedServices.map((s) => {
                        const linkedDoc = docsByServiceOrId.get(s.id) || (s.doc_id ? docsByServiceOrId.get(s.doc_id) : undefined);
                        const prov = providers.find(p => p.id === s.provider_id);
                        const sigs = resolveSessionSignatures(s, linkedDoc, prov);
                        const billing = resolveBillingReadiness(s);
                        const planned = s.planned_duration_minutes || s.duration_minutes || 60;
                        const actual = s.actual_duration_minutes || s.duration_minutes || 60;
                        const timeStr = formatSessionTime(s.start_time, s.end_time);

                        return (
                          <tr
                            key={s.id}
                            className="hover:bg-[#20314a] odd:bg-[#18263a] even:bg-[#101827] transition-colors duration-150 cursor-pointer"
                            onClick={() => {
                              if (linkedDoc || s.doc_id) {
                                onNavigateTab('clinical');
                              } else {
                                onSelectClient(s.client_id);
                              }
                            }}
                          >
                            {/* Client (Priority 1) */}
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSelectClient(s.client_id);
                                }}
                                className="font-semibold text-white hover:text-sky-300 transition-colors text-left flex items-center gap-1 group/btn cursor-pointer"
                              >
                                <span>{s.first_name} {s.last_name}</span>
                                <ExternalLink className="w-3 h-3 text-slate-500 group-hover/btn:text-sky-400 opacity-0 group-hover/btn:opacity-100 transition-opacity" />
                              </button>
                            </td>

                            {/* DOS & Time (Priority 2) */}
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <div className="font-mono font-semibold text-slate-200">{s.dos}</div>
                              {timeStr !== '—' && (
                                <div className="text-[10px] font-mono text-slate-400 flex items-center gap-1 mt-0.5">
                                  <Clock className="w-2.5 h-2.5 text-slate-500" />
                                  <span>{timeStr}</span>
                                </div>
                              )}
                            </td>

                            {/* Treating Provider (Priority 3) */}
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <div className="text-slate-200 font-medium">{s.provider_name || '—'}</div>
                              {(prov?.role || s.provider_role) && (
                                <span className="inline-block mt-0.5 px-1.5 py-0.2 rounded bg-slate-800 text-slate-300 border border-slate-700/60 font-mono text-[9px] font-bold">
                                  {prov?.role || s.provider_role}
                                </span>
                              )}
                            </td>

                            {/* CPT */}
                            <td className="hidden lg:table-cell px-3 py-2.5 font-mono text-sky-400 font-semibold whitespace-nowrap">
                              {s.service_code}
                            </td>

                            {/* Planned vs Actual Duration */}
                            <td className="hidden lg:table-cell px-3 py-2.5 font-mono whitespace-nowrap">
                              <span className="font-bold text-slate-100">{actual}m act</span>
                              {planned !== actual && (
                                <span className="text-[10.5px] text-slate-400 ml-1">({planned}m pl)</span>
                              )}
                            </td>

                            {/* Linked Clinical Note */}
                            <td className="hidden xl:table-cell px-3 py-2.5 max-w-[200px]">
                              {linkedDoc || s.doc_id ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onNavigateTab('clinical');
                                  }}
                                  className="text-left group/note cursor-pointer"
                                  title="Open in Clinical Documents"
                                >
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <span className="px-1.5 py-0.2 rounded bg-indigo-950/80 text-indigo-300 border border-indigo-700/60 text-[9.5px] font-bold font-mono">
                                      {linkedDoc?.doc_type || s.doc_type || 'SOAP'}
                                    </span>
                                    <span className="text-[10px] text-emerald-400 font-semibold">
                                      {linkedDoc?.status || s.doc_status || 'FINALIZED'}
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-slate-400 group-hover/note:text-sky-300 truncate mt-0.5">
                                    {linkedDoc?.title || s.doc_title || s.completion_notes || 'View Note'}
                                  </div>
                                </button>
                              ) : (
                                <span className="text-slate-400 truncate block" title={s.completion_notes || ''}>
                                  {s.completion_notes || '—'}
                                </span>
                              )}
                            </td>

                            {/* Guardian Sig */}
                            <td className="hidden 2xl:table-cell px-2.5 py-2.5 whitespace-nowrap">
                              <span
                                className={`px-2 py-0.5 rounded border text-[9.5px] font-bold inline-flex items-center gap-1 ${sigs.guardian.badgeClass}`}
                                title={sigs.guardian.status === 'SIGNED' && sigs.guardian.signer ? `Signed by: ${sigs.guardian.signer}` : (sigs.guardian.signer || sigs.guardian.label)}
                              >
                                {sigs.guardian.label}
                              </span>
                            </td>

                            {/* Supervisor Sig */}
                            <td className="hidden 2xl:table-cell px-2.5 py-2.5 whitespace-nowrap">
                              <span
                                className={`px-2 py-0.5 rounded border text-[9.5px] font-bold inline-flex items-center gap-1 ${sigs.supervisor.badgeClass}`}
                                title={sigs.supervisor.signer ? `Signed by: ${sigs.supervisor.signer}` : sigs.supervisor.label}
                              >
                                {sigs.supervisor.label}
                              </span>
                            </td>

                            {/* Clinician Sig */}
                            <td className="hidden 2xl:table-cell px-2.5 py-2.5 whitespace-nowrap">
                              <span
                                className={`px-2 py-0.5 rounded border text-[9.5px] font-bold inline-flex items-center gap-1 ${sigs.clinician.badgeClass}`}
                                title={sigs.clinician.signer ? `Signed by: ${sigs.clinician.signer}` : sigs.clinician.label}
                              >
                                {sigs.clinician.label}
                              </span>
                            </td>

                            {/* Billing Readiness (Priority 4) */}
                            <td className="px-2.5 py-2.5 whitespace-nowrap">
                              <span
                                className={`px-2 py-0.5 rounded border text-[9.5px] font-bold inline-flex items-center gap-1 ${billing.badgeClass}`}
                                title={billing.holdReasons.length > 0 ? billing.holdReasons.join(', ') : billing.label}
                              >
                                {billing.status === 'HELD' && <AlertTriangle className="w-2.5 h-2.5 text-rose-400" />}
                                <span>{billing.label}</span>
                              </span>
                            </td>

                            {/* Action */}
                            <td className="px-3 py-2.5 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end space-x-1.5">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectClient(s.client_id);
                                  }}
                                  className="px-2 py-0.5 text-[11px] font-semibold rounded bg-slate-900/90 hover:bg-slate-800 text-sky-300 hover:text-white border border-sky-500/35 transition-colors cursor-pointer"
                                  title="View Client Chart"
                                >
                                  Chart
                                </button>
                                {(linkedDoc || s.doc_id) && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onNavigateTab('clinical');
                                    }}
                                    className="px-2 py-0.5 text-[11px] font-semibold rounded bg-indigo-950/80 hover:bg-indigo-900 text-indigo-200 hover:text-white border border-indigo-500/40 transition-colors cursor-pointer"
                                    title="View Clinical Note"
                                  >
                                    Note
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Stacked Cards (Under md) */}
                <div className="md:hidden divide-y divide-slate-800/60">
                  {filteredCompletedServices.map((s, idx) => {
                    const linkedDoc = docsByServiceOrId.get(s.id) || (s.doc_id ? docsByServiceOrId.get(s.doc_id) : undefined);
                    const prov = providers.find(p => p.id === s.provider_id);
                    const sigs = resolveSessionSignatures(s, linkedDoc, prov);
                    const billing = resolveBillingReadiness(s);
                    const planned = s.planned_duration_minutes || s.duration_minutes || 60;
                    const actual = s.actual_duration_minutes || s.duration_minutes || 60;
                    const timeStr = formatSessionTime(s.start_time, s.end_time);

                    return (
                      <div
                        key={`mb-cs-${s.id}`}
                        className={`p-3.5 space-y-2.5 ${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} hover:from-[#223554] hover:to-[#1a2b42] transition-colors`}
                      >
                        {/* Line 1: Primary Entity (Client Name) + Billing Readiness */}
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => onSelectClient(s.client_id)}
                            className="font-bold text-white text-[13.5px] hover:text-sky-300 transition-colors text-left truncate flex items-center gap-1.5 cursor-pointer"
                          >
                            <span>{s.first_name} {s.last_name}</span>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                          </button>
                          <span className={`px-2 py-0.5 rounded border text-[9.5px] font-bold shrink-0 leading-none ${billing.badgeClass}`}>
                            {billing.label}
                          </span>
                        </div>

                        {/* Line 2: DOS · Time · CPT · Duration */}
                        <div className="text-xs text-slate-300 font-mono flex items-center gap-1.5 flex-wrap">
                          <span className="text-slate-400 font-medium">DOS:</span>
                          <span className="text-white font-semibold">{s.dos}</span>
                          {timeStr !== '—' && (
                            <>
                              <span className="text-slate-600">·</span>
                              <span className="text-slate-300">{timeStr}</span>
                            </>
                          )}
                          <span className="text-slate-600">·</span>
                          <span className="text-sky-400 font-semibold">{s.service_code}</span>
                          <span className="text-slate-600">·</span>
                          <span className="text-slate-200">{actual}m act</span>
                          {planned !== actual && (
                            <span className="text-slate-500 text-[11px]">({planned}m pl)</span>
                          )}
                        </div>

                        {/* Line 3: Treating Provider */}
                        <div className="text-xs text-slate-400 flex items-center gap-1.5 flex-wrap">
                          <span className="text-slate-500 font-medium">Provider:</span>
                          <span className="text-slate-200 font-medium">{s.provider_name || '—'}</span>
                          {(prov?.role || s.provider_role) && (
                            <span className="px-1.5 py-0.2 rounded bg-slate-800 text-slate-300 border border-slate-700/60 font-mono text-[9px] font-bold">
                              {prov?.role || s.provider_role}
                            </span>
                          )}
                        </div>

                        {/* Line 4: Linked Clinical Note */}
                        {(linkedDoc || s.doc_id || s.completion_notes) && (
                          <div className="text-[11px] text-slate-300 bg-slate-900/70 p-2.5 rounded-lg border border-slate-800 flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="px-1.5 py-0.2 rounded bg-indigo-950/80 text-indigo-300 border border-indigo-700/60 text-[9px] font-bold font-mono">
                                  {linkedDoc?.doc_type || s.doc_type || 'NOTE'}
                                </span>
                                <span className="text-[10px] text-emerald-400 font-semibold">
                                  {linkedDoc?.status || s.doc_status || 'FINALIZED'}
                                </span>
                              </div>
                              <div className="text-slate-300 text-xs mt-1 truncate">
                                {linkedDoc?.title || s.doc_title || s.completion_notes}
                              </div>
                            </div>
                            {(linkedDoc || s.doc_id) && (
                              <button
                                type="button"
                                onClick={() => onNavigateTab('clinical')}
                                className="text-sky-400 hover:text-sky-300 text-[10px] font-semibold underline shrink-0 cursor-pointer"
                              >
                                View Doc ↗
                              </button>
                            )}
                          </div>
                        )}

                        {/* Line 5: Signatures Pill Grid */}
                        <div className="pt-1 flex items-center gap-1.5 flex-wrap text-[10px]">
                          <span className="text-slate-500 font-medium">Signatures:</span>
                          <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold ${sigs.guardian.badgeClass}`}>
                            Grd: {sigs.guardian.label}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold ${sigs.supervisor.badgeClass}`}>
                            Sup: {sigs.supervisor.label}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold ${sigs.clinician.badgeClass}`}>
                            Clin: {sigs.clinician.label}
                          </span>
                        </div>

                        {/* Line 6: Mobile Actions */}
                        <div className="pt-1 flex items-center justify-end gap-2 border-t border-slate-800/60">
                          <button
                            type="button"
                            onClick={() => onSelectClient(s.client_id)}
                            className="px-2.5 py-1 text-xs font-semibold rounded bg-slate-900/90 text-sky-300 border border-sky-500/35 hover:bg-slate-800 cursor-pointer"
                          >
                            Client Chart
                          </button>
                          {(linkedDoc || s.doc_id) && (
                            <button
                              type="button"
                              onClick={() => onNavigateTab('clinical')}
                              className="px-2.5 py-1 text-xs font-semibold rounded bg-indigo-950/80 text-indigo-300 border border-indigo-500/40 hover:bg-indigo-900 cursor-pointer"
                            >
                              Clinical Document
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: CALENDAR & UPCOMING (VISIT PLANNER) */}
      {activeTab === 'CALENDAR' && (
        <div className="space-y-4">
          <div className="glass-panel rounded-xl border border-slate-800 overflow-hidden mobile-ops-shell">
            {scheduledServices.length === 0 ? (
              <div className="p-12 text-center text-slate-400 space-y-2">
                <Calendar className="w-8 h-8 text-slate-600 mx-auto stroke-[1.5]" />
                <div className="text-sm font-semibold text-slate-300">No Upcoming Visits</div>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Planned visits will appear here.
                </p>
              </div>
            ) : (
              <>
                {/* Desktop/Tablet Table (md and up): Preserved unchanged */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-slate-700/70 text-[9.5px] font-bold text-slate-300 uppercase tracking-wider bg-gradient-to-b from-[#111928] to-[#0b101b] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
                        <th className="p-3 font-semibold">DOS</th>
                        <th className="p-3 font-semibold">Time</th>
                        <th className="p-3 font-semibold">Client Name</th>
                        <th className="p-3 font-semibold">Treating Provider</th>
                        <th className="p-3 font-semibold">Code</th>
                        <th className="p-3 font-semibold">Duration</th>
                        <th className="p-3 font-semibold">Weekly Cadence</th>
                        <th className="p-3 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60">
                      {scheduledServices.map((s) => {
                        const weekStats = getWeeklySessionStats(s.client_id, s.dos, services, assignments);
                        return (
                          <tr key={s.id} className="hover:bg-[#283e62] odd:bg-[#1c2c46] even:bg-[#223554] transition-colors">
                            <td className="p-3 font-mono text-slate-300 font-semibold">{s.dos}</td>
                            <td className="p-3 font-mono text-slate-400">{s.start_time || '—'}</td>
                            <td className="p-3">
                              <button
                                onClick={() => onSelectClient(s.client_id)}
                                className="font-semibold text-white hover:text-sky-400 transition-colors"
                              >
                                {s.first_name} {s.last_name}
                              </button>
                            </td>
                            <td className="p-3 text-slate-200">{s.provider_name}</td>
                            <td className="p-3 font-mono text-sky-400">{s.service_code || '92507'}</td>
                            <td className="p-3 font-mono text-slate-300">{s.duration_minutes || 60}m</td>
                            <td className="p-3">
                              {weekStats.freqInfo.isWeekly ? (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono border ${
                                  weekStats.exceeds
                                    ? 'bg-rose-950/60 text-rose-300 border-rose-500/50'
                                    : weekStats.totalCount === weekStats.freqInfo.count
                                    ? 'bg-emerald-950/60 text-emerald-300 border-emerald-500/40'
                                    : weekStats.totalCount > 0
                                    ? 'bg-amber-950/60 text-amber-300 border-amber-500/40'
                                    : 'bg-slate-800/80 text-slate-400 border-slate-700/80'
                                }`}>
                                  {weekStats.totalCount} of {weekStats.freqInfo.count} planned
                                  {weekStats.exceeds && <span className="ml-1 text-[10px] text-rose-300 font-bold">(! over)</span>}
                                </span>
                              ) : (
                                <span className="text-slate-400 font-mono text-[11px]">
                                  {weekStats.totalCount} planned • {weekStats.rawFreq || 'Per Auth'}
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex items-center justify-end space-x-1.5">
                                <button
                                  onClick={() => handleDeleteScheduledVisit(s.id)}
                                  className="p-1 rounded text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                                  title="Cancel and remove planned visit"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile-Only Stacked Rows (No desktop columns, no sideways scrolling) */}
                <div className="md:hidden divide-y divide-slate-800/60 bg-transparent">
                  {scheduledServices.map((s, idx) => {
                    const weekStats = getWeeklySessionStats(s.client_id, s.dos, services, assignments);
                    const formattedDate = formatPlannerVisitDate(s.dos);
                    const formattedTime = formatPlannerVisitTime(s.start_time);
                    const modalityLabel = s.modality === 'TELETHERAPY'
                      ? 'Teletherapy'
                      : s.modality === 'ONSITE'
                      ? 'Home'
                      : s.location_type || 'Home';

                    return (
                      <div key={s.id} className={`p-3.5 space-y-2 ${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} transition-colors`}>
                        {/* Row 1: Client Name (tappable to Client Chart) */}
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => onSelectClient(s.client_id)}
                            className="font-bold text-white text-[13.5px] hover:text-sky-300 transition-colors text-left truncate flex items-center gap-1.5"
                          >
                            <span>{s.first_name} {s.last_name}</span>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                          </button>
                          <span className="font-mono text-[10px] text-sky-400 px-1.5 py-0.5 rounded bg-sky-950/60 border border-sky-800/40 shrink-0">
                            {s.service_code || '92507'} • {s.duration_minutes || 60}m
                          </span>
                        </div>

                        {/* Row 2: Date · Time */}
                        <div className="text-xs text-sky-300 font-mono font-medium flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                          <span>{formattedDate}{formattedTime ? ` · ${formattedTime}` : ''}</span>
                        </div>

                        {/* Row 3: Provider · Location/Modality */}
                        <div className="text-xs text-slate-300 flex items-center gap-1.5">
                          <span className="font-medium text-slate-200">
                            {s.provider_name}{s.provider_role ? `, ${s.provider_role}` : ''}
                          </span>
                          <span className="text-slate-600">·</span>
                          <span className="text-slate-400">{modalityLabel}</span>
                        </div>

                        {/* Row 4: Status / Cadence badge & Action buttons */}
                        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-800/80">
                          <div>
                            {weekStats.freqInfo.isWeekly ? (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-mono border ${
                                weekStats.exceeds
                                  ? 'bg-rose-950/60 text-rose-300 border-rose-500/50'
                                  : weekStats.totalCount === weekStats.freqInfo.count
                                  ? 'bg-emerald-950/60 text-emerald-300 border-emerald-500/40'
                                  : weekStats.totalCount > 0
                                  ? 'bg-amber-950/60 text-amber-300 border-amber-500/40'
                                  : 'bg-slate-800/80 text-slate-400 border-slate-700/80'
                              }`}>
                                {weekStats.totalCount} of {weekStats.freqInfo.count} planned
                                {weekStats.exceeds && <span className="ml-1 text-[9.5px] text-rose-300 font-bold">(! over)</span>}
                              </span>
                            ) : (
                              <span className="text-slate-400 font-mono text-[10.5px]">
                                {weekStats.totalCount} planned • {weekStats.rawFreq || 'Per Auth'}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center space-x-1.5 ml-auto">
                            <button
                              type="button"
                              onClick={() => handleDeleteScheduledVisit(s.id)}
                              className="p-1 rounded text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors active:scale-95"
                              title="Cancel and remove planned visit"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* PLAN VISIT MODAL */}
      {showPlanVisitModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl border border-slate-200/90 shadow-xl p-6 space-y-4 text-slate-800">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3.5">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-violet-600" />
                <h3 className="text-sm font-bold text-slate-900 tracking-tight">Plan Upcoming Therapy Visit</h3>
              </div>
              <button
                onClick={() => setShowPlanVisitModal(false)}
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Frequency awareness preview for selected client & DOS */}
            {(() => {
              if (!planVisitForm.client_id || !planVisitForm.dos) return null;
              const stats = getWeeklySessionStats(planVisitForm.client_id, planVisitForm.dos, services, assignments);
              return (
                <div className={`p-3.5 rounded-xl border text-xs space-y-1.5 ${
                  stats.exceeds
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : 'bg-white border-violet-200 text-slate-800'
                }`}>
                  <div className="flex items-center justify-between font-semibold">
                    <span className="flex items-center space-x-1.5">
                      {stats.exceeds ? (
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                      ) : (
                        <Calendar className="w-4 h-4 text-violet-600" />
                      )}
                      <span>Weekly Frequency Overview</span>
                    </span>
                    <span className="font-mono text-[11px] text-slate-500">Week of {stats.weekRange.start}</span>
                  </div>
                  <div className="text-slate-700 text-[11px] leading-relaxed">
                    {stats.freqInfo.isWeekly ? (
                      <>
                        <span>Authorized/Expected: </span>
                        <strong className="text-slate-900">{stats.freqInfo.count} session(s)/week</strong>.
                        <span> Currently scheduled this week: </span>
                        <strong className="text-slate-900">{stats.totalCount}</strong>.
                        {stats.totalCount >= stats.freqInfo.count && (
                          <div className="text-amber-800 mt-1 font-medium">
                            Note: Adding this visit will be session #{stats.totalCount + 1} this week. (Non-blocking: you may still proceed).
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <span>Frequency Context: </span>
                        <strong className="text-slate-900">{stats.rawFreq || 'Per Authorization / Flexible'}</strong>.
                        <span> Planned this week: </span>
                        <strong className="text-slate-900">{stats.totalCount} session(s)</strong>.
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

            <form onSubmit={handleSavePlanVisit} className="space-y-3.5 text-xs">
              <div>
                <label className="block text-slate-600 mb-1 font-medium">Select Client *</label>
                <select
                  required
                  value={planVisitForm.client_id}
                  onChange={(e) => {
                    const cid = e.target.value;
                    const asn = assignments.find(a => a.client_id === cid);
                    setPlanVisitForm({
                      ...planVisitForm,
                      client_id: cid,
                      provider_id: asn?.provider_id || planVisitForm.provider_id || '',
                      duration_minutes: asn?.expected_duration_minutes || planVisitForm.duration_minutes || 60,
                      service_code: asn?.auth_service_code || planVisitForm.service_code || '92507'
                    });
                  }}
                  className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 text-xs"
                >
                  <option value="">Choose client...</option>
                  {eligibleActiveClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.last_name}, {c.first_name} [{c.id}]{c.ahcccs_id ? ` (AHCCCS: ${c.ahcccs_id})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 mt-1 italic">
                  Only active eligible clients are available for visit planning.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 mb-1 font-medium">Date of Service (DOS) *</label>
                  <input
                    type="date"
                    required
                    value={planVisitForm.dos}
                    onChange={(e) => setPlanVisitForm({ ...planVisitForm, dos: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 font-mono text-xs [color-scheme:light]"
                  />
                </div>

                <div>
                  <label className="block text-slate-600 mb-1 font-medium">Planned Start Time</label>
                  <input
                    type="time"
                    value={planVisitForm.start_time}
                    onChange={(e) => setPlanVisitForm({ ...planVisitForm, start_time: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 font-mono text-xs [color-scheme:light]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 mb-1 font-medium">Treating Provider</label>
                  <select
                    value={planVisitForm.provider_id}
                    onChange={(e) => setPlanVisitForm({ ...planVisitForm, provider_id: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 text-xs"
                  >
                    <option value="">Assigned / Default Provider</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.role})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-600 mb-1 font-medium">Service / CPT Code</label>
                  <select
                    value={planVisitForm.service_code}
                    onChange={(e) => setPlanVisitForm({ ...planVisitForm, service_code: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 font-mono text-xs"
                  >
                    <option value="92507">92507 - Treatment of Speech (Standard)</option>
                    <option value="92523">92523 - Evaluation of Speech Sound & Language</option>
                    <option value="92521">92521 - Evaluation of Speech Fluency</option>
                    <option value="92524">92524 - Behavioral and Qualitative Voice Analysis</option>
                    <option value="92526">92526 - Oral Function Therapy / Dysphagia</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-600 mb-1 font-medium">Duration (Minutes)</label>
                <input
                  type="number"
                  min="15"
                  step="15"
                  value={planVisitForm.duration_minutes}
                  onChange={(e) => setPlanVisitForm({ ...planVisitForm, duration_minutes: parseInt(e.target.value, 10) || 60 })}
                  className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 font-mono text-xs"
                />
              </div>

              <div>
                <label className="block text-slate-600 mb-1 font-medium">Planning / Visit Notes</label>
                <textarea
                  rows={2}
                  value={planVisitForm.completion_notes}
                  onChange={(e) => setPlanVisitForm({ ...planVisitForm, completion_notes: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 text-xs"
                  placeholder="Optional visit objectives or location details..."
                />
              </div>

              {planVisitSyncWarning && (
                <p className="text-xs font-semibold text-amber-800">{planVisitSyncWarning}</p>
              )}
              <div className="flex justify-end space-x-2.5 pt-3.5 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowPlanVisitModal(false)}
                  className="px-3.5 py-2 rounded-xl text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={Boolean(planVisitSyncWarning)}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-xs transition-all border border-slate-700 active:scale-95 flex items-center space-x-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Plan Visit</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ASSIGN CLIENT MODAL */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel w-full max-w-lg rounded-xl border border-slate-700 p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center space-x-2">
                <UserCheck className="w-5 h-5 text-sky-400" />
                <h3 className="text-sm font-bold text-white">Assign Client to Treating Provider</h3>
              </div>
              <button
                onClick={() => setShowAssignModal(false)}
                className="text-slate-400 hover:text-white p-1 rounded hover:bg-white/5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveAssignment} className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-400 mb-1">Select Client</label>
                <select
                  required
                  value={assignForm.client_id}
                  onChange={(e) => setAssignForm({ ...assignForm, client_id: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-sky-500"
                >
                  <option value="">Choose client...</option>
                  {clients
                    .filter((c) => (c.status || 'ACTIVE').toUpperCase() === 'ACTIVE')
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.last_name}, {c.first_name} [{c.id}]{c.ahcccs_id ? ` (AHCCCS: ${c.ahcccs_id})` : ''}
                      </option>
                    ))}
                </select>
                <p className="text-[10px] text-slate-500 mt-1 italic">
                  Only active clients can receive treating provider assignments.
                </p>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">Treating Provider</label>
                <select
                  required
                  value={assignForm.provider_id}
                  onChange={(e) => handleProviderSelectInForm(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-sky-500"
                >
                  <option value="">Choose provider...</option>
                  {providers.map((p) => {
                    const rateLabel = (p.rate_per_session !== null && p.rate_per_session !== undefined && Number(p.rate_per_session) > 0)
                      ? ` - $${p.rate_per_session}/session`
                      : ' - Standard Pay Schedule';
                    return (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.role}){rateLabel}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Supervising SLP (if provider is SLPA) */}
              <div>
                <label className="block text-slate-400 mb-1">
                  Supervising SLP (Required for Assistants)
                </label>
                <select
                  value={assignForm.supervisor_id}
                  onChange={(e) => setAssignForm({ ...assignForm, supervisor_id: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-sky-500"
                >
                  <option value="">None / Direct SLP</option>
                  {supervisors.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.role})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Expected Frequency</label>
                  <input
                    type="text"
                    value={assignForm.expected_frequency}
                    onChange={(e) => setAssignForm({ ...assignForm, expected_frequency: e.target.value })}
                    placeholder="e.g. 2x/week"
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Planned Duration (Minutes)</label>
                  <input
                    type="number"
                    value={assignForm.expected_duration_minutes}
                    onChange={(e) => setAssignForm({ ...assignForm, expected_duration_minutes: Number(e.target.value) })}
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-sky-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">Assignment Notes (Optional)</label>
                <textarea
                  rows={2}
                  value={assignForm.notes}
                  onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                  placeholder="Caseload scheduling notes..."
                  className="w-full bg-slate-950 border border-slate-700 rounded p-2 text-slate-200 focus:outline-none focus:border-sky-500"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  className="px-3 py-1.5 rounded bg-slate-800 text-slate-300 hover:bg-slate-700 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded bg-sky-600 hover:bg-sky-500 text-white font-bold"
                >
                  Save Assignment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RECORD COMPLETED SERVICE MODAL */}
      {showRecordServiceModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel w-full max-w-lg rounded-xl border border-slate-700 p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <h3 className="text-sm font-bold text-white">Record Completed Service</h3>
              </div>
              <button
                onClick={() => setShowRecordServiceModal(false)}
                className="text-slate-400 hover:text-white p-1 rounded hover:bg-white/5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleRecordCompletedService} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Date of Service (DOS)</label>
                  <input
                    type="date"
                    required
                    value={recordServiceForm.dos}
                    onChange={(e) => setRecordServiceForm({ ...recordServiceForm, dos: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-emerald-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Service Code</label>
                  <select
                    value={recordServiceForm.service_code}
                    onChange={(e) => setRecordServiceForm({ ...recordServiceForm, service_code: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-emerald-500 font-mono"
                  >
                    <option value="92507">92507 (Speech Treatment)</option>
                    <option value="92523">92523 (Comprehensive Eval)</option>
                    <option value="92521">92521 (Fluency Eval)</option>
                    <option value="92526">92526 (Feeding/Oral Motor)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">Client</label>
                <select
                  required
                  value={recordServiceForm.client_id}
                  onChange={(e) => setRecordServiceForm({ ...recordServiceForm, client_id: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-emerald-500"
                >
                  <option value="">Choose client...</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.last_name}, {c.first_name} [{c.id}]{c.ahcccs_id ? ` (AHCCCS: ${c.ahcccs_id})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">Rendering Provider</label>
                <select
                  required
                  value={recordServiceForm.provider_id}
                  onChange={(e) => setRecordServiceForm({ ...recordServiceForm, provider_id: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-emerald-500"
                >
                  <option value="">Choose provider...</option>
                  {providers.map((p) => {
                    const rateLabel = (p.rate_per_session !== null && p.rate_per_session !== undefined && Number(p.rate_per_session) > 0)
                      ? ` - $${p.rate_per_session}/session`
                      : ' - Standard Pay Schedule';
                    return (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.role}){rateLabel}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Planned Duration (Min)</label>
                  <input
                    type="number"
                    value={recordServiceForm.planned_duration_minutes}
                    onChange={(e) => setRecordServiceForm({ ...recordServiceForm, planned_duration_minutes: Number(e.target.value) })}
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Actual Rendered (Min)</label>
                  <input
                    type="number"
                    value={recordServiceForm.actual_duration_minutes}
                    onChange={(e) => setRecordServiceForm({ ...recordServiceForm, actual_duration_minutes: Number(e.target.value) })}
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-emerald-500 font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">Session Notes</label>
                <textarea
                  rows={3}
                  value={recordServiceForm.completion_notes}
                  onChange={(e) => setRecordServiceForm({ ...recordServiceForm, completion_notes: e.target.value })}
                  placeholder="Session notes..."
                  className="w-full bg-slate-950 border border-slate-700 rounded p-2 text-slate-200 focus:outline-none focus:border-emerald-500"
                />
              </div>

              {recordServiceSyncWarning && (
                <p className="text-xs font-semibold text-amber-300">{recordServiceSyncWarning}</p>
              )}
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowRecordServiceModal(false)}
                  className="px-3 py-1.5 rounded bg-slate-800 text-slate-300 hover:bg-slate-700 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={Boolean(recordServiceSyncWarning)}
                  className="px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Record Completed Service
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {manualSoapAssignment && (
        <ManualSoapApprovalModal
          assignment={manualSoapAssignment}
          onClose={() => setManualSoapAssignment(null)}
          onSaved={async () => {
            setManualSoapAssignment(null);
            await fetchData();
          }}
        />
      )}

      {/* Add Therapist / Clinical Provider Modal */}
      <AddTherapistModal
        isOpen={showAddTherapistModal}
        onClose={() => setShowAddTherapistModal(false)}
        onTherapistAdded={handleTherapistAdded}
        existingProviders={providers}
      />
    </div>
  );
};
